import React, { useEffect, useState, useCallback } from "react";
import { http, formatErr } from "../lib/api";
import { Loader2, Trash2, Plus, BadgeCheck, Shield, FileWarning, History, Zap, ShieldCheck, MapPin, Heart, CheckCircle, AlertCircle, Music, CalendarClock, Megaphone, Users, Upload, Download, Bot, FileText, Sparkles, PlayCircle, BookOpen, RefreshCw, ExternalLink, Star, CheckCircle2, XCircle, Clock, Info, ChevronDown, UserCog, Trophy, Crown, Image, Video, Send, ShoppingBag, Building2, HeartHandshake, Lock, Flag } from "lucide-react";
import { StoreAdminContent } from "./store/StoreAdmin";
import { MarketplaceAdminContent } from "./marketplace/MarketplaceAdmin";
import { toast } from "sonner";
import VerifiedBadge from "../components/VerifiedBadge";

const KEYS = [
  { key: "ccc_ranks", label: "CCC Ranks & Titles" },
  { key: "badges", label: "Recognition Badges" },
  { key: "event_categories", label: "Event Categories" },
  { key: "service_types", label: "Service Team Types" },
  { key: "prayer_categories", label: "Prayer Categories" },
  { key: "job_categories", label: "Job Categories" },
  { key: "report_reasons", label: "Report Reasons" },
  { key: "livestream_providers", label: "Livestream Providers" },
];

function SettingsManager() {
  const [activeKey, setActiveKey] = useState("ccc_ranks");
  const [items, setItems] = useState([]);
  const [newLabel, setNewLabel] = useState("");
  const load = () => http.get(`/settings/${activeKey}`).then((r) => setItems(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [activeKey]);
  const add = async () => {
    if (!newLabel.trim()) return;
    try { await http.post("/settings", { key: activeKey, label: newLabel, order: items.length }); setNewLabel(""); toast.success("Added"); load(); } catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (id) => { try { await http.delete(`/settings/${id}`); load(); } catch (e) { toast.error(formatErr(e)); } };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {KEYS.map((k) => (
          <button key={k.key} onClick={() => setActiveKey(k.key)} data-testid={`setting-tab-${k.key}`} className={`px-3 py-1.5 text-sm rounded-md border ${activeKey === k.key ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]" : "border-[var(--border-default)]"}`}>{k.label}</button>
        ))}
      </div>
      <div className="card-surface p-5">
        <div className="flex gap-2 mb-4">
          <input className="input-clean" placeholder="Add new value..." value={newLabel} onChange={(e) => setNewLabel(e.target.value)} data-testid="setting-new-input" />
          <button onClick={add} className="btn-primary inline-flex items-center gap-1" data-testid="setting-add"><Plus size={14} /> Add</button>
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          {items.map((it) => (
            <div key={it.id} className="flex items-center justify-between border border-[var(--border-default)] rounded-md px-3 py-2 text-sm" data-testid={`setting-item-${it.id}`}>
              <span>{it.label}</span>
              <button onClick={() => remove(it.id)} className="text-red-700"><Trash2 size={14} /></button>
            </div>
          ))}
          {items.length === 0 && <div className="text-sm text-[var(--text-secondary)]">No items.</div>}
        </div>
      </div>
    </div>
  );
}

const BLANK_PARISH = { name: "", country: "", state: "", city: "", address: "", shepherd_name: "", phone: "", website: "", service_times: "", description: "", image_url: "", lat: "", lng: "", status: "active", join_mode: "request_only", choir_enabled: true, ministries_enabled: true };

function ParishForm({ value, onChange }) {
  const f = (k) => (e) => onChange({ ...value, [k]: e.type === "checkbox" ? e.target.checked : e.target.value });
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <input className="input-clean sm:col-span-2" placeholder="Parish name *" value={value.name} onChange={f("name")} data-testid="ap-name" />
      <input className="input-clean" placeholder="Country *" value={value.country} onChange={f("country")} data-testid="ap-country" />
      <input className="input-clean" placeholder="State / Region" value={value.state} onChange={f("state")} />
      <input className="input-clean" placeholder="City *" value={value.city} onChange={f("city")} data-testid="ap-city" />
      <input className="input-clean" placeholder="Shepherd name" value={value.shepherd_name} onChange={f("shepherd_name")} />
      <input className="input-clean sm:col-span-2" placeholder="Full address" value={value.address} onChange={f("address")} />
      <input className="input-clean" placeholder="Phone" value={value.phone} onChange={f("phone")} />
      <input className="input-clean" placeholder="Website URL" value={value.website} onChange={f("website")} />
      <input className="input-clean sm:col-span-2" placeholder="Service / worship times (e.g. Sun 9am, Wed 6pm)" value={value.service_times} onChange={f("service_times")} />
      <input className="input-clean sm:col-span-2" placeholder="Image URL (parish banner or logo)" value={value.image_url} onChange={f("image_url")} />
      <input className="input-clean" placeholder="Latitude (e.g. 6.5244)" type="number" step="any" value={value.lat} onChange={f("lat")} data-testid="ap-lat" />
      <input className="input-clean" placeholder="Longitude (e.g. 3.3792)" type="number" step="any" value={value.lng} onChange={f("lng")} data-testid="ap-lng" />
      <select className="input-clean" value={value.join_mode} onChange={f("join_mode")} data-testid="ap-join-mode">
        <option value="request_only">Join Mode: By Request (admin approval required)</option>
        <option value="location_based">Join Mode: Location-based (auto-approve same country)</option>
        <option value="open">Join Mode: Open (anyone joins directly)</option>
      </select>
      <select className="input-clean" value={value.status} onChange={f("status")} data-testid="ap-status">
        <option value="active">Status: Active</option>
        <option value="inactive">Status: Inactive</option>
      </select>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={Boolean(value.choir_enabled)} onChange={f("choir_enabled")} className="w-4 h-4 accent-[var(--brand-primary)]" /> Choir enabled
      </label>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={Boolean(value.ministries_enabled)} onChange={f("ministries_enabled")} className="w-4 h-4 accent-[var(--brand-primary)]" /> Ministries enabled
      </label>
      <textarea className="input-clean sm:col-span-2 min-h-[70px]" placeholder="About this parish…" value={value.description} onChange={f("description")} />
    </div>
  );
}

function ParishesManager() {
  const [list, setList] = useState([]);
  const [allList, setAllList] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [form, setForm] = useState(BLANK_PARISH);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [stats, setStats] = useState({});
  const [creating, setCreating] = useState(false);

  const load = () => {
    http.get("/parishes", { params: { limit: 500 } }).then((r) => {
      setAllList(r.data);
      r.data.forEach((p) => {
        http.get(`/parishes/${p.id}/stats`).then((s) => setStats((prev) => ({ ...prev, [p.id]: s.data }))).catch(() => {});
      });
    });
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (statusFilter === "all") {
      setList(allList);
    } else {
      setList(allList.filter((p) => p.status === statusFilter));
    }
  }, [allList, statusFilter]);

  const pendingCount = allList.filter((p) => p.status === "pending_review").length;

  const create = async () => {
    if (!form.name || !form.country || !form.city) return toast.error("Name, country, and city are required.");
    setCreating(true);
    try {
      await http.post("/parishes", { ...form, lat: form.lat ? parseFloat(form.lat) : null, lng: form.lng ? parseFloat(form.lng) : null });
      toast.success("Parish created"); setForm(BLANK_PARISH); load();
    } catch (e) { toast.error(formatErr(e)); } finally { setCreating(false); }
  };

  const saveEdit = async (pid) => {
    try {
      await http.patch(`/parishes/${pid}`, { ...editForm, lat: editForm.lat ? parseFloat(editForm.lat) : null, lng: editForm.lng ? parseFloat(editForm.lng) : null });
      toast.success("Parish updated"); setEditing(null); setEditForm(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const approve = async (p) => {
    try { await http.patch(`/parishes/${p.id}`, { status: "active" }); toast.success(`"${p.name}" is now active`); load(); } catch (e) { toast.error(formatErr(e)); }
  };

  const toggleStatus = async (p) => {
    const next = p.status === "active" ? "inactive" : "active";
    try { await http.patch(`/parishes/${p.id}`, { status: next }); toast.success(`Parish ${next}`); load(); } catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (pid, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try { await http.delete(`/parishes/${pid}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatErr(e)); }
  };

  const STATUS_FILTERS = [
    { k: "all",            label: "All" },
    { k: "active",         label: "Active" },
    { k: "pending_review", label: "Pending Review" },
    { k: "inactive",       label: "Inactive" },
  ];

  const STATUS_COLOR = {
    active:         "bg-emerald-50 text-emerald-700 border-emerald-200",
    pending_review: "bg-amber-50 text-amber-700 border-amber-200",
    inactive:       "bg-gray-100 text-gray-600 border-gray-200",
  };

  return (
    <div className="space-y-6">
      {/* Pending review alert */}
      {pendingCount > 0 && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
          <AlertCircle size={16} className="text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-800">
              {pendingCount} parish{pendingCount !== 1 ? "es" : ""} awaiting review
            </div>
            <div className="text-xs text-amber-700 mt-0.5">
              These were submitted by users during sign-up. Review and activate or delete them.
            </div>
          </div>
          <button onClick={() => setStatusFilter("pending_review")} className="text-xs px-3 py-1.5 rounded-md bg-amber-600 text-white hover:bg-amber-700 shrink-0">
            Review now
          </button>
        </div>
      )}

      {/* Create form */}
      <div className="card-surface p-5">
        <h3 className="font-display text-xl text-[var(--brand-primary)] mb-4 flex items-center gap-2"><Plus size={16} /> Create new parish</h3>
        <ParishForm value={form} onChange={setForm} />
        <div className="flex justify-end mt-4">
          <button onClick={create} disabled={creating} className="btn-primary inline-flex items-center gap-2" data-testid="ap-create">
            {creating && <Loader2 size={14} className="animate-spin" />} Create Parish
          </button>
        </div>
      </div>

      {/* Parish list */}
      <div className="space-y-3">
        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 items-center">
          {STATUS_FILTERS.map((f) => (
            <button key={f.k} onClick={() => setStatusFilter(f.k)}
              className={`px-3 py-1.5 rounded-md border text-xs font-medium transition-colors inline-flex items-center gap-1.5 ${
                statusFilter === f.k ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]" : "border-[var(--border-default)] text-[var(--text-secondary)]"
              }`}
              data-testid={`parish-filter-${f.k}`}
            >
              {f.label}
              {f.k === "pending_review" && pendingCount > 0 && (
                <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold ${statusFilter === f.k ? "bg-white text-amber-700" : "bg-amber-500 text-white"}`}>
                  {pendingCount}
                </span>
              )}
            </button>
          ))}
          <span className="text-xs text-[var(--text-tertiary)] ml-auto">{list.length} shown</span>
        </div>

        {list.map((p) => (
          <div key={p.id} className={`card-surface ${p.status === "pending_review" ? "border-l-4 border-l-amber-400" : ""}`} data-testid={`admin-parish-${p.id}`}>
            {editing === p.id ? (
              <div className="p-5 space-y-4">
                <ParishForm value={editForm} onChange={setEditForm} />
                <div className="flex gap-2 justify-end">
                  <button onClick={() => { setEditing(null); setEditForm(null); }} className="px-4 py-2 text-sm border border-[var(--border-default)] rounded-md">Cancel</button>
                  <button onClick={() => saveEdit(p.id)} className="btn-primary text-sm" data-testid={`save-parish-${p.id}`}>Save changes</button>
                </div>
              </div>
            ) : (
              <div className="p-4 flex flex-wrap items-start gap-3">
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium text-[var(--brand-primary)]">{p.name}</div>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border font-semibold ${STATUS_COLOR[p.status] || STATUS_COLOR.inactive}`}>
                      {p.status === "pending_review" ? "Pending Review" : p.status}
                    </span>
                    <span className="text-[10px] text-[var(--text-tertiary)] uppercase">{p.join_mode?.replace(/_/g, " ")}</span>
                    {p.source === "user_onboarding" && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200 font-semibold">User submitted</span>
                    )}
                  </div>
                  <div className="text-xs text-[var(--text-tertiary)] mt-0.5">{[p.city, p.state, p.country].filter(Boolean).join(", ")}</div>
                  {p.shepherd_name && <div className="text-xs text-[var(--text-secondary)] mt-0.5">Shepherd: {p.shepherd_name}</div>}
                  {p.contributed_by_name && <div className="text-xs text-[var(--text-tertiary)] mt-0.5">Submitted by: {p.contributed_by_name}</div>}
                  {p.phone && <div className="text-xs text-[var(--text-tertiary)] mt-0.5">Phone: {p.phone}</div>}
                  {p.address && <div className="text-xs text-[var(--text-tertiary)] mt-0.5">{p.address}</div>}
                  {stats[p.id] && <div className="text-xs text-[var(--brand-accent)] mt-1">{stats[p.id].member_count} member{stats[p.id].member_count !== 1 ? "s" : ""} {stats[p.id].pending_count > 0 ? `• ${stats[p.id].pending_count} pending` : ""}</div>}
                </div>
                <div className="flex flex-wrap gap-2">
                  {p.status === "pending_review" && (
                    <button onClick={() => approve(p)} className="text-xs px-3 py-1.5 rounded-md border border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-semibold inline-flex items-center gap-1" data-testid={`approve-parish-${p.id}`}>
                      <CheckCircle size={12} /> Approve
                    </button>
                  )}
                  <button onClick={() => { setEditing(p.id); setEditForm({ ...BLANK_PARISH, ...p, lat: p.lat ?? "", lng: p.lng ?? "" }); }} className="text-xs px-3 py-1.5 rounded-md border border-[var(--border-default)] hover:border-[var(--brand-primary)]" data-testid={`edit-parish-${p.id}`}>Edit</button>
                  {p.status !== "pending_review" && (
                    <button onClick={() => toggleStatus(p)} className={`text-xs px-3 py-1.5 rounded-md border ${p.status === "active" ? "border-amber-300 text-amber-700 hover:bg-amber-50" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}`} data-testid={`toggle-parish-${p.id}`}>
                      {p.status === "active" ? "Deactivate" : "Activate"}
                    </button>
                  )}
                  <button onClick={() => remove(p.id, p.name)} className="text-xs px-3 py-1.5 rounded-md border border-red-200 text-red-700 hover:bg-red-50" data-testid={`delete-parish-${p.id}`}><Trash2 size={12} /></button>
                </div>
              </div>
            )}
          </div>
        ))}
        {list.length === 0 && <div className="card-surface p-6 text-sm text-[var(--text-secondary)] text-center">No parishes in this category.</div>}
      </div>
    </div>
  );
}

function ApprovalsManager() {
  const [pending, setPending] = useState([]);
  const [choirPending, setChoirPending] = useState([]);
  const [servicePending, setServicePending] = useState([]);
  const load = () => {
    http.get("/memberships/pending").then((r) => setPending(r.data)).catch(() => {});
    http.get("/choir/pending").then((r) => setChoirPending(r.data)).catch(() => {});
    http.get("/service/pending").then((r) => setServicePending(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const approveMembership = async (id) => { try { await http.post(`/memberships/${id}/approve`); toast.success("Approved"); load(); } catch (e) { toast.error(formatErr(e)); } };
  const rejectMembership = async (id) => { try { await http.post(`/memberships/${id}/reject`); load(); } catch {} };
  const verifyChoir = async (id) => { try { await http.post(`/choir/${id}/verify`); toast.success("Choir member verified"); load(); } catch (e) { toast.error(formatErr(e)); } };
  const promote = async (id) => { try { await http.post(`/choir/${id}/promote`); toast.success("Promoted to choir director"); load(); } catch (e) { toast.error(formatErr(e)); } };
  const approveService = async (id) => { try { await http.post(`/service/${id}/approve`); toast.success("Service approved"); load(); } catch (e) { toast.error(formatErr(e)); } };

  return (
    <div className="space-y-6">
      <section>
        <h3 className="font-display text-xl mb-2 text-[var(--brand-primary)]">Pending parish memberships</h3>
        {pending.length === 0 ? <div className="text-sm text-[var(--text-secondary)]">No pending requests.</div> :
          <div className="grid md:grid-cols-2 gap-3">
            {pending.map((m) => (
              <div key={m.id} className="card-surface p-4 text-sm" data-testid={`pending-${m.id}`}>
                <div className="font-medium">{m.user?.name} → {m.parish?.name}</div>
                <div className="text-xs text-[var(--text-tertiary)]">{m.user?.ccc_rank} • {m.user?.country}</div>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => approveMembership(m.id)} className="btn-primary text-xs px-3 py-1" data-testid={`approve-${m.id}`}>Approve</button>
                  <button onClick={() => rejectMembership(m.id)} className="text-xs px-3 py-1 rounded-md border border-[var(--border-default)]">Reject</button>
                </div>
              </div>
            ))}
          </div>}
      </section>
      <section>
        <h3 className="font-display text-xl mb-2 text-[var(--brand-primary)]">Pending choir verifications</h3>
        {choirPending.length === 0 ? <div className="text-sm text-[var(--text-secondary)]">No pending choir requests.</div> :
          <div className="grid md:grid-cols-2 gap-3">
            {choirPending.map((c) => (
              <div key={c.id} className="card-surface p-4 text-sm" data-testid={`choir-pending-${c.id}`}>
                <div className="font-medium">{c.user_name} <span className="text-[var(--text-tertiary)]">({c.voice_part})</span></div>
                <div className="text-xs text-[var(--text-tertiary)]">{c.parish?.name}</div>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => verifyChoir(c.id)} className="btn-primary text-xs px-3 py-1 inline-flex items-center gap-1" data-testid={`verify-choir-${c.id}`}><BadgeCheck size={12} /> Verify</button>
                  <button onClick={() => promote(c.id)} className="text-xs px-3 py-1 rounded-md border border-[var(--brand-accent)] text-[var(--brand-accent)]" data-testid={`promote-${c.id}`}>Promote to Director</button>
                </div>
              </div>
            ))}
          </div>}
      </section>
      <section>
        <h3 className="font-display text-xl mb-2 text-[var(--brand-primary)]">Pending service requests</h3>
        {servicePending.length === 0 ? <div className="text-sm text-[var(--text-secondary)]">No pending service requests.</div> :
          <div className="grid md:grid-cols-2 gap-3">
            {servicePending.map((s) => (
              <div key={s.id} className="card-surface p-4 text-sm" data-testid={`service-pending-${s.id}`}>
                <div className="font-medium">{s.user_name} → {s.service_type}</div>
                <button onClick={() => approveService(s.id)} className="btn-primary text-xs px-3 py-1 mt-2">Approve</button>
              </div>
            ))}
          </div>}
      </section>
    </div>
  );
}

function UsersManager() {
  const [users, setUsers] = useState([]);
  const [parishes, setParishes] = useState([]);
  const [badges, setBadges] = useState([]);
  const [verifyReasonMap, setVerifyReasonMap] = useState({});
  const load = () => http.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => {
    load();
    http.get("/parishes").then((r) => setParishes(r.data));
    http.get("/settings/badges").then((r) => setBadges(r.data));
  }, []);
  const setRole = async (uid, role, parish_id) => { try { await http.post(`/admin/users/${uid}/role`, { role, parish_id }); toast.success("Role updated"); load(); } catch (e) { toast.error(formatErr(e)); } };
  const awardBadge = async (uid, badge) => { try { await http.post(`/admin/users/${uid}/badge`, { badge }); toast.success("Badge awarded"); load(); } catch (e) { toast.error(formatErr(e)); } };
  const verify = async (uid) => {
    const reason = verifyReasonMap[uid] || "Verified by Super Admin";
    try { await http.post(`/admin/users/${uid}/verify`, { reason }); toast.success("Verified"); load(); } catch (e) { toast.error(formatErr(e)); }
  };
  const unverify = async (uid) => {
    try { await http.delete(`/admin/users/${uid}/verify`); toast.success("Verification removed"); load(); } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="space-y-3">
      {users.map((u) => (
        <div key={u.id} className="card-surface p-4 text-sm" data-testid={`user-${u.id}`}>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[180px]">
              <div className="flex items-center gap-2">
                <span className="font-medium text-[var(--brand-primary)]">{u.name}</span>
                {u.verified && <VerifiedBadge size="xs" reason={u.verified_reason} />}
              </div>
              <div className="text-xs text-[var(--text-tertiary)]">{u.email} • {u.ccc_rank} • {u.country}</div>
              {u.badges?.length > 0 && <div className="text-[10px] mt-1 text-[var(--brand-accent)]">{u.badges.join(" • ")}</div>}
            </div>
            <select defaultValue={u.role} onChange={(e) => setRole(u.id, e.target.value, u.assigned_parish_id)} className="input-clean text-xs max-w-[180px]" data-testid={`role-${u.id}`}>
              {["member", "moderator", "shepherd", "parish_admin", "super_admin"].map((r) => (<option key={r} value={r}>{r}</option>))}
            </select>
            <select defaultValue={u.assigned_parish_id || ""} onChange={(e) => setRole(u.id, u.role, e.target.value)} className="input-clean text-xs max-w-[200px]">
              <option value="">No parish assigned</option>
              {parishes.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
            </select>
            <select onChange={(e) => e.target.value && awardBadge(u.id, e.target.value)} defaultValue="" className="input-clean text-xs max-w-[180px]" data-testid={`badge-select-${u.id}`}>
              <option value="">Award badge…</option>
              {badges.map((b) => (<option key={b.id} value={b.label}>{b.label}</option>))}
            </select>
          </div>
          {/* Verification controls */}
          <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-[var(--border-default)]">
            <span className="text-xs text-[var(--text-tertiary)] font-semibold uppercase tracking-wide">Verified badge:</span>
            {u.verified ? (
              <>
                <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 size={11} /> Verified{u.verified_reason ? ` · ${u.verified_reason}` : ""}</span>
                <button onClick={() => unverify(u.id)} className="text-xs px-2.5 py-1 rounded-md border border-red-200 text-red-600 hover:bg-red-50" data-testid={`unverify-${u.id}`}>Remove</button>
              </>
            ) : (
              <>
                <input
                  type="text"
                  className="input-clean text-xs max-w-[220px]"
                  placeholder="Verification reason (optional)"
                  value={verifyReasonMap[u.id] || ""}
                  onChange={(e) => setVerifyReasonMap((m) => ({ ...m, [u.id]: e.target.value }))}
                  data-testid={`verify-reason-${u.id}`}
                />
                <button onClick={() => verify(u.id)} className="text-xs px-2.5 py-1 rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1" data-testid={`verify-btn-${u.id}`}>
                  <CheckCircle2 size={11} /> Verify
                </button>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Parish Admin Requests Manager ───────────────────────────────────────────
const REQ_STATUS_META = {
  pending:    { label: "Pending",          color: "bg-amber-50 text-amber-700 border-amber-200" },
  approved:   { label: "Approved",         color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected:   { label: "Rejected",         color: "bg-red-50 text-red-700 border-red-200" },
  needs_info: { label: "More Info Needed", color: "bg-blue-50 text-blue-700 border-blue-200" },
  deferred:   { label: "Deferred",         color: "bg-gray-100 text-gray-600 border-gray-200" },
};

function ParishAdminRequestsManager() {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [reviewNote, setReviewNote] = useState({});
  const [busy, setBusy] = useState({});

  const load = (s) => {
    setLoading(true);
    const params = s && s !== "all" ? { status: s } : {};
    http.get("/admin/parish-admin-requests", { params })
      .then((r) => setRequests(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(filter); }, [filter]);

  const review = async (rid, action) => {
    setBusy((b) => ({ ...b, [rid]: action }));
    try {
      const note = reviewNote[rid] || "";
      await http.post(`/admin/parish-admin-requests/${rid}/review`, { action, note });
      const labels = { approve: "Approved", reject: "Rejected", needs_info: "Needs info sent", defer: "Deferred" };
      toast.success(labels[action] || "Done");
      load(filter);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy((b) => { const n = { ...b }; delete n[rid]; return n; }); }
  };

  const FILTERS = ["all", "pending", "approved", "rejected", "needs_info", "deferred"];

  return (
    <div className="space-y-5">
      {/* Status filter tabs */}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-md border text-xs capitalize transition-colors ${
              filter === s ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]" : "border-[var(--border-default)] text-[var(--text-secondary)]"
            }`}>
            {s === "all" ? "All" : REQ_STATUS_META[s]?.label || s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-2 animate-pulse">{[1,2,3].map((n) => <div key={n} className="card-surface h-24 rounded-xl" />)}</div>
      ) : requests.length === 0 ? (
        <div className="card-surface p-10 text-center">
          <CheckCircle2 size={28} className="mx-auto text-emerald-500 mb-2" />
          <p className="text-sm text-[var(--text-secondary)]">No requests in this category.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((r) => {
            const meta = REQ_STATUS_META[r.status] || REQ_STATUS_META.pending;
            return (
              <div key={r.id} className="card-surface overflow-hidden" data-testid={`par-req-${r.id}`}>
                <div className="border-l-4 border-[var(--brand-accent)] px-5 py-4 space-y-3">
                  {/* Request header */}
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-medium text-[var(--brand-primary)]">{r.user_name}</span>
                        {r.ccc_rank && <span className="text-xs text-[var(--brand-accent)]">{r.ccc_rank}</span>}
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold uppercase tracking-wide ${meta.color}`}>{meta.label}</span>
                      </div>
                      <div className="text-xs text-[var(--text-tertiary)]">{r.user_email} · {new Date(r.created_at).toLocaleDateString()}</div>
                      <div className="text-sm font-medium text-[var(--brand-primary)] mt-1">Parish: {r.parish_name || r.parish_id}</div>
                    </div>
                  </div>

                  {/* Request body */}
                  <div className="space-y-2">
                    <div>
                      <div className="text-xs font-semibold text-[var(--text-tertiary)] uppercase tracking-wide mb-0.5">Why they want to be Parish Admin</div>
                      <p className="text-sm text-[var(--text-secondary)]">{r.reason}</p>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-[var(--text-tertiary)] uppercase tracking-wide mb-0.5">Request note</div>
                      <p className="text-sm text-[var(--text-secondary)]">{r.note}</p>
                    </div>
                    {r.comments && (
                      <div>
                        <div className="text-xs font-semibold text-[var(--text-tertiary)] uppercase tracking-wide mb-0.5">Supporting comments</div>
                        <p className="text-sm text-[var(--text-secondary)]">{r.comments}</p>
                      </div>
                    )}
                    {r.review_note && (
                      <div className={`rounded-lg px-3 py-2 text-xs border ${meta.color}`}>
                        <span className="font-semibold">Previous review note: </span>{r.review_note}
                        {r.reviewer_name && <span className="text-[var(--text-tertiary)] ml-1">— {r.reviewer_name}</span>}
                      </div>
                    )}
                  </div>

                  {/* Review actions (only for pending or needs_info) */}
                  {(r.status === "pending" || r.status === "needs_info" || r.status === "deferred") && (
                    <div className="pt-2 border-t border-[var(--border-default)] space-y-2">
                      <input
                        type="text"
                        className="input-clean text-sm"
                        placeholder="Review note (shown to member — required for reject/needs_info)"
                        value={reviewNote[r.id] || ""}
                        onChange={(e) => setReviewNote((m) => ({ ...m, [r.id]: e.target.value }))}
                        data-testid={`review-note-${r.id}`}
                      />
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => review(r.id, "approve")}
                          disabled={!!busy[r.id]}
                          className="btn-primary text-xs inline-flex items-center gap-1"
                          data-testid={`approve-req-${r.id}`}
                        >
                          {busy[r.id] === "approve" ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />} Approve
                        </button>
                        <button
                          onClick={() => review(r.id, "needs_info")}
                          disabled={!!busy[r.id]}
                          className="text-xs px-3 py-1.5 rounded-md border border-blue-300 text-blue-700 hover:bg-blue-50"
                          data-testid={`needs-info-req-${r.id}`}
                        >
                          {busy[r.id] === "needs_info" ? <Loader2 size={11} className="animate-spin" /> : <Info size={11} />} Needs Info
                        </button>
                        <button
                          onClick={() => review(r.id, "defer")}
                          disabled={!!busy[r.id]}
                          className="text-xs px-3 py-1.5 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50"
                          data-testid={`defer-req-${r.id}`}
                        >
                          {busy[r.id] === "defer" ? <Loader2 size={11} className="animate-spin" /> : <Clock size={11} />} Defer
                        </button>
                        <button
                          onClick={() => review(r.id, "reject")}
                          disabled={!!busy[r.id]}
                          className="text-xs px-3 py-1.5 rounded-md border border-red-200 text-red-600 hover:bg-red-50"
                          data-testid={`reject-req-${r.id}`}
                        >
                          {busy[r.id] === "reject" ? <Loader2 size={11} className="animate-spin" /> : <XCircle size={11} />} Reject
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ModerationManager() {
  const [reports, setReports] = useState([]);
  const load = () => http.get("/admin/reports", { params: { status: "open" } }).then((r) => setReports(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const act = async (id, action) => { try { await http.post(`/admin/reports/${id}/resolve`, { action }); toast.success(`Report ${action}`); load(); } catch (e) { toast.error(formatErr(e)); } };
  if (reports.length === 0) return <div className="card-surface p-5 text-sm text-[var(--text-secondary)]">No open reports.</div>;
  return (
    <div className="space-y-3">
      {reports.map((r) => (
        <div key={r.id} className="card-surface p-4 text-sm" data-testid={`report-${r.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-medium text-[var(--brand-primary)]">{r.target_type} flagged: <span className="text-[var(--brand-accent)]">{r.reason}</span></div>
              <div className="text-xs text-[var(--text-tertiary)]">By {r.reporter_name} • {new Date(r.created_at).toLocaleString()} • target id: {r.target_id}</div>
              {r.note && <div className="text-xs mt-1">{r.note}</div>}
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={() => act(r.id, "dismiss")} className="text-xs px-3 py-1 rounded-md border border-[var(--border-default)]" data-testid={`dismiss-${r.id}`}>Dismiss</button>
              <button onClick={() => act(r.id, "hide")} className="text-xs px-3 py-1 rounded-md border border-orange-700 text-orange-700" data-testid={`hide-${r.id}`}>Hide</button>
              <button onClick={() => act(r.id, "delete")} className="text-xs px-3 py-1 rounded-md bg-red-700 text-white" data-testid={`delete-${r.id}`}>Delete</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function AuditManager() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { http.get("/admin/audit-logs").then((r) => setLogs(r.data)).catch(() => {}); }, []);
  return (
    <div className="card-surface p-5">
      {logs.length === 0 ? <div className="text-sm text-[var(--text-secondary)]">No audit entries yet.</div> :
        <div className="space-y-2 text-sm">
          {logs.map((l) => (
            <div key={l.id} className="flex items-start gap-3 border-b border-[var(--border-default)] pb-2 last:border-0" data-testid={`audit-${l.id}`}>
              <History size={14} className="text-[var(--text-tertiary)] mt-1" />
              <div className="flex-1">
                <div><span className="font-medium">{l.actor_name}</span> <span className="text-[var(--text-tertiary)]">{l.action}</span> → <span className="text-[var(--brand-primary)]">{l.target}</span></div>
                <div className="text-xs text-[var(--text-tertiary)]">{new Date(l.created_at).toLocaleString()} {l.details ? `• ${JSON.stringify(l.details)}` : ""}</div>
              </div>
            </div>
          ))}
        </div>}
    </div>
  );
}

function IntegrationsManager() {
  const [items, setItems] = useState([]);
  const FIELDS = [
    { group: "AI Assistant (Free — Groq / Llama)", label: "ai_provider", title: "AI Provider", desc: "groq (free, default) · mistral (free tier) · together (free tier) · ollama (self-hosted) · openai (paid). Sign up free at console.groq.com.", secret: false, placeholder: "groq" },
    { label: "ai_api_key", title: "AI API Key", desc: "Paste your free Groq API key here (gsk_…). Get it free at console.groq.com — no credit card needed.", secret: true, placeholder: "gsk_…" },
    { label: "ai_model", title: "AI Model (optional override)", desc: "Leave blank to use the provider default. Groq default: llama-3.3-70b-versatile. Mistral default: mistral-small-latest.", secret: false, placeholder: "llama-3.3-70b-versatile" },
    { label: "ai_base_url", title: "AI Base URL (optional override)", desc: "Only needed for Ollama or custom deployments. Leave blank for all hosted providers.", secret: false, placeholder: "http://localhost:11434/v1" },
    { label: "daily_posts_enabled", title: "Daily Posts enabled", desc: "Set to 'true' to auto-post daily devotion/prayer/verse/music to the Global Feed at 6am UTC. Requires AI API key above.", secret: false, placeholder: "true" },
    { group: "Parish Join Rules", label: "global_join_mode", title: "Global join mode", desc: "per_parish = each parish decides; open = anyone joins directly; location_based = same-country auto-approve; request_only = always admin approval.", secret: false, placeholder: "per_parish" },
    { label: "max_parish_memberships", title: "Max parish memberships per user", desc: "How many active + pending memberships a user may hold (default: 2).", secret: false, placeholder: "2" },
    { label: "join_state_match_required", title: "Require state match for location-based join", desc: "Set true to also require state/region match in addition to country (default: false).", secret: false, placeholder: "false" },
    { group: "Email (Resend)", label: "resend_api_key", title: "Resend API key", desc: "Used for password reset emails and notifications.", secret: true, placeholder: "re_xxx" },
    { label: "resend_from_email", title: "Resend 'From' email", desc: "Verified sender, e.g. CelestialPeopleMeeet <noreply@celestialpeoplemeet.com>", secret: false, placeholder: "noreply@celestialpeoplemeet.com" },
    { group: "Maps & Directions", label: "google_maps_api_key_public", title: "Google Maps JS API key (public)", desc: "Used by the parish detail embedded map. Must have Maps Embed API enabled.", secret: false, placeholder: "AIza…" },
    { group: "Storage (Cloudflare R2)", label: "cloudflare_r2_account_id", title: "Cloudflare R2 account ID", desc: "From your Cloudflare dashboard.", secret: false },
    { label: "cloudflare_r2_access_key_id", title: "Cloudflare R2 access key ID", desc: "", secret: true },
    { label: "cloudflare_r2_secret_access_key", title: "Cloudflare R2 secret", desc: "", secret: true },
    { label: "cloudflare_r2_bucket", title: "Cloudflare R2 bucket name", desc: "", secret: false, placeholder: "celestial-media" },
    { label: "cloudflare_r2_public_url", title: "R2 public base URL", desc: "Custom domain or pub-...r2.dev URL", secret: false, placeholder: "https://media.celestialpeoplemeet.com" },
    { group: "Push Notifications", label: "vapid_public_key", title: "VAPID public key (auto-generated)", desc: "Click Generate VAPID below if empty.", secret: false, readonly: true },
  ];
  const load = () => http.get("/integrations").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const save = async (label, value) => {
    try { await http.post("/integrations", { label, value }); toast.success(`${label} saved`); load(); } catch (e) { toast.error(formatErr(e)); }
  };
  const test = async (provider) => {
    try { const { data } = await http.post(`/integrations/test/${provider}`, {}); toast[data.ok ? "success" : "error"](data.message); load(); } catch (e) { toast.error(formatErr(e)); }
  };
  const valueOf = (label) => items.find((i) => i.label === label)?.meta?.value || "";
  const masked = (label) => items.find((i) => i.label === label)?.masked_value || "";
  const has = (label) => items.find((i) => i.label === label)?.has_value || false;

  return (
    <div className="space-y-4">
      <div className="card-surface p-5 flex flex-wrap gap-3 items-center">
        <button onClick={() => test("vapid")} className="btn-primary inline-flex items-center gap-2" data-testid="gen-vapid"><Zap size={14} /> Generate / fetch VAPID keys</button>
        <button onClick={() => test("resend")} className="btn-accent inline-flex items-center gap-2" data-testid="test-resend">Test Resend email</button>
        <span className="text-xs text-[var(--text-tertiary)]">Generated keys are stored in admin settings — never hardcoded.</span>
      </div>
      <div className="grid gap-3">
        {FIELDS.map((f) => (
          <React.Fragment key={f.label}>
            {f.group && <div className="pt-2 pb-1 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--brand-accent)] border-b border-[var(--border-default)]">{f.group}</div>}
            <IntegrationRow field={f} currentValue={valueOf(f.label)} masked={masked(f.label)} hasValue={has(f.label)} onSave={save} />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function IntegrationRow({ field, currentValue, masked, hasValue, onSave }) {
  const [val, setVal] = useState(currentValue || "");
  useEffect(() => { setVal(currentValue || ""); }, [currentValue]);
  return (
    <div className="card-surface p-4" data-testid={`integration-${field.label}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-[var(--brand-primary)]">{field.title}</div>
          <div className="text-xs text-[var(--text-tertiary)]">{field.desc}</div>
          {field.secret && hasValue && <div className="text-[10px] text-[var(--brand-accent)] mt-1">Currently set: {masked}</div>}
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <input
            type={field.secret ? "password" : "text"}
            className="input-clean text-sm flex-1 sm:w-80"
            placeholder={field.placeholder || (field.secret ? "Paste new value…" : "")}
            value={val}
            onChange={(e) => setVal(e.target.value)}
            readOnly={field.readonly}
            data-testid={`integration-input-${field.label}`}
          />
          {!field.readonly && <button onClick={() => onSave(field.label, val)} className="btn-primary text-sm" data-testid={`integration-save-${field.label}`}>Save</button>}
        </div>
      </div>
    </div>
  );
}

function ShepherdEndorsementManager() {
  const [users, setUsers] = useState([]);
  const [parishes, setParishes] = useState([]);
  const [pick, setPick] = useState({});
  const [endorsements, setEndorsements] = useState([]);

  const load = () => {
    http.get("/admin/users").then((r) => setUsers(r.data));
    http.get("/parishes").then((r) => setParishes(r.data));
    http.get("/shepherds").then((r) => setEndorsements(r.data));
  };
  useEffect(() => { load(); }, []);

  const endorse = async (uid) => {
    const parish_id = pick[uid];
    if (!parish_id) return toast.error("Pick a parish first");
    try { await http.post(`/admin/users/${uid}/endorse-shepherd`, { parish_id, note: "Verified shepherd for this parish." }); toast.success("Endorsed"); load(); } catch (e) { toast.error(formatErr(e)); }
  };
  const revoke = async (eid) => {
    try { await http.delete(`/admin/endorsements/${eid}`); toast.success("Revoked"); load(); } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-6">
      <section>
        <h3 className="font-display text-xl mb-2 text-[var(--brand-primary)] flex items-center gap-2"><ShieldCheck size={18} className="text-[var(--brand-accent)]" /> Active Endorsements</h3>
        {endorsements.length === 0 ? <div className="card-surface p-4 text-sm text-[var(--text-secondary)]">No endorsements yet.</div> :
          <div className="grid sm:grid-cols-2 gap-3">
            {endorsements.map((e) => (
              <div key={e.id} className="card-surface p-4 text-sm border-l-4 border-[var(--brand-accent)]" data-testid={`endorsement-${e.id}`}>
                <div className="font-medium">{e.user_name} <span className="text-[var(--brand-accent)] text-xs">Verified Shepherd</span></div>
                <div className="text-xs text-[var(--text-tertiary)]">{e.parish_name} • by {e.endorser_name}</div>
                <button onClick={() => revoke(e.id)} className="text-xs text-red-700 mt-2" data-testid={`revoke-${e.id}`}>Revoke</button>
              </div>
            ))}
          </div>}
      </section>
      <section>
        <h3 className="font-display text-xl mb-2 text-[var(--brand-primary)]">Endorse a member</h3>
        <div className="space-y-2">
          {users.filter((u) => u.role !== "super_admin").map((u) => (
            <div key={u.id} className="card-surface p-3 text-sm flex flex-wrap items-center gap-3" data-testid={`endorse-row-${u.id}`}>
              <div className="flex-1 min-w-[180px]">
                <div className="font-medium text-[var(--brand-primary)]">{u.name}</div>
                <div className="text-xs text-[var(--text-tertiary)]">{u.ccc_rank} • {u.country}</div>
              </div>
              <select value={pick[u.id] || ""} onChange={(ev) => setPick({ ...pick, [u.id]: ev.target.value })} className="input-clean text-xs max-w-[240px]" data-testid={`endorse-parish-${u.id}`}>
                <option value="">Select parish</option>
                {parishes.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
              </select>
              <button onClick={() => endorse(u.id)} className="btn-accent text-xs px-3 py-1" data-testid={`endorse-btn-${u.id}`}>Endorse as Shepherd</button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function PrayerModerationManager() {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = () => {
    setLoading(true);
    http.get("/admin/prayers/moderation")
      .then((r) => setQueue(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const act = async (pid, action) => {
    setBusy((b) => ({ ...b, [pid]: action }));
    try {
      await http.patch(`/admin/prayers/${pid}`, { action });
      toast.success(action === "remove" ? "Prayer removed" : action === "archive" ? "Prayer archived" : "Prayer restored");
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy((b) => { const n = { ...b }; delete n[pid]; return n; }); }
  };

  if (loading) return <div className="text-sm text-[var(--text-secondary)] animate-pulse">Loading moderation queue…</div>;

  if (queue.length === 0) {
    return (
      <div className="card-surface p-8 text-center space-y-2">
        <CheckCircle size={28} className="mx-auto text-emerald-500" />
        <p className="font-display text-xl text-[var(--brand-primary)]">All clear</p>
        <p className="text-sm text-[var(--text-secondary)]">No prayer requests have been reported.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-sm text-[var(--text-secondary)] flex items-center gap-2">
        <AlertCircle size={15} className="text-amber-600" />
        {queue.length} prayer request{queue.length !== 1 ? "s" : ""} flagged for review, sorted by report count.
      </div>
      {queue.map((p) => (
        <div key={p.id} className="card-surface overflow-hidden" data-testid={`prayer-mod-${p.id}`}>
          <div className="border-l-4 border-amber-400 px-5 py-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                    {p.report_count} report{p.report_count !== 1 ? "s" : ""}
                  </span>
                  <span className="text-xs text-[var(--text-tertiary)]">{p.scope === "parish" ? "Parish wall" : "Global wall"}</span>
                  <span className={`text-xs font-semibold uppercase rounded-full px-2 py-0.5 border ${
                    p.status === "removed" ? "bg-red-50 text-red-700 border-red-200" :
                    p.status === "archived" ? "bg-gray-100 text-gray-500 border-gray-200" :
                    "bg-blue-50 text-blue-700 border-blue-200"
                  }`}>{p.status}</span>
                </div>
                <h4 className="font-display text-lg text-[var(--brand-primary)]">{p.title}</h4>
                <p className="text-sm text-[var(--text-secondary)] mt-1 line-clamp-3">{p.body}</p>
                <div className="text-xs text-[var(--text-tertiary)] mt-2">
                  By {p.anonymous ? "Anonymous" : p.user_name} •
                  Report reasons: <span className="text-amber-700">{[...new Set(p.report_reasons)].join(", ") || "none specified"}</span>
                </div>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                {p.status !== "removed" && (
                  <button
                    onClick={() => act(p.id, "remove")}
                    disabled={!!busy[p.id]}
                    className="text-xs px-3 py-1.5 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                    data-testid={`prayer-remove-${p.id}`}
                  >
                    {busy[p.id] === "remove" ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                    Remove
                  </button>
                )}
                {p.status !== "archived" && p.status !== "removed" && (
                  <button
                    onClick={() => act(p.id, "archive")}
                    disabled={!!busy[p.id]}
                    className="text-xs px-3 py-1.5 rounded-md border border-amber-400 text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                    data-testid={`prayer-archive-${p.id}`}
                  >
                    Archive
                  </button>
                )}
                {(p.status === "removed" || p.status === "archived") && (
                  <button
                    onClick={() => act(p.id, "restore")}
                    disabled={!!busy[p.id]}
                    className="text-xs px-3 py-1.5 rounded-md border border-emerald-400 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                    data-testid={`prayer-restore-${p.id}`}
                  >
                    Restore
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Choir Hub Manager ────────────────────────────────────────────────────
function ChoirHubManager() {
  const [parishes, setParishes] = useState([]);
  const [selectedPid, setSelectedPid] = useState("");
  const [pending, setPending] = useState([]);
  const [rehearsals, setRehearsals] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [subTab, setSubTab] = useState("pending");
  const [busy, setBusy] = useState({});

  useEffect(() => {
    http.get("/parishes").then((r) => {
      setParishes(r.data);
      if (r.data[0]) setSelectedPid(r.data[0].id);
    }).catch(() => {});
    http.get("/choir/pending").then((r) => setPending(r.data)).catch(() => {});
  }, []);

  const loadParishData = useCallback(() => {
    if (!selectedPid) return;
    http.get("/rehearsals", { params: { parish_id: selectedPid } }).then((r) => setRehearsals(r.data)).catch(() => {});
    http.get("/choir/announcements", { params: { parish_id: selectedPid } }).then((r) => setAnnouncements(r.data)).catch(() => {});
  }, [selectedPid]);

  useEffect(() => { loadParishData(); }, [loadParishData]);

  const verify = async (cid) => {
    setBusy((b) => ({ ...b, [cid]: true }));
    try { await http.post(`/choir/${cid}/verify`); toast.success("Member verified"); http.get("/choir/pending").then((r) => setPending(r.data)); } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy((b) => { const n = { ...b }; delete n[cid]; return n; }); }
  };

  const promote = async (cid) => {
    try { await http.post(`/choir/${cid}/promote`); toast.success("Promoted to director"); } catch (e) { toast.error(formatErr(e)); }
  };

  const deleteRehearsal = async (rid) => {
    try { await http.delete(`/rehearsals/${rid}`); loadParishData(); toast.success("Rehearsal removed"); } catch (e) { toast.error(formatErr(e)); }
  };

  const deleteAnnouncement = async (aid) => {
    try { await http.delete(`/choir/announcements/${aid}`); loadParishData(); toast.success("Announcement removed"); } catch (e) { toast.error(formatErr(e)); }
  };

  const parishPending = pending.filter((c) => c.parish_id === selectedPid);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 flex-wrap">
        <select className="input-clean max-w-xs" value={selectedPid} onChange={(e) => setSelectedPid(e.target.value)}>
          <option value="">Select parish</option>
          {parishes.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <div className="flex gap-1.5">
          {["pending", "rehearsals", "announcements"].map((s) => (
            <button key={s} onClick={() => setSubTab(s)} className={`px-3 py-1.5 rounded-md border text-xs capitalize ${subTab === s ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]" : "border-[var(--border-default)] text-[var(--text-secondary)]"}`}>
              {s}
              {s === "pending" && pending.length > 0 && <span className="ml-1.5 bg-amber-500 text-white text-[10px] rounded-full px-1.5 py-0.5">{pending.length}</span>}
            </button>
          ))}
        </div>
      </div>

      {subTab === "pending" && (
        <div className="space-y-3">
          <div className="text-sm text-[var(--text-secondary)]">{parishPending.length} pending choir verification request{parishPending.length !== 1 ? "s" : ""} for this parish.</div>
          {parishPending.length === 0 ? (
            <div className="card-surface p-8 text-center space-y-2">
              <CheckCircle size={28} className="mx-auto text-emerald-500" />
              <p className="text-sm text-[var(--text-secondary)]">No pending choir requests for this parish.</p>
            </div>
          ) : parishPending.map((c) => (
            <div key={c.id} className="card-surface p-4 flex items-center gap-3 flex-wrap" data-testid={`admin-choir-pending-${c.id}`}>
              <div className="w-9 h-9 rounded-full bg-[var(--bg-subtle)] grid place-items-center font-medium shrink-0">{(c.user_name || "?").slice(0, 1)}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[var(--brand-primary)]">{c.user_name}</div>
                <div className="text-xs text-[var(--text-tertiary)]">{c.voice_part} · {c.parish?.name}</div>
                {c.note && <div className="text-xs text-[var(--text-secondary)] italic mt-0.5">"{c.note}"</div>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => verify(c.id)} disabled={!!busy[c.id]} className="btn-primary text-xs inline-flex items-center gap-1" data-testid={`admin-verify-${c.id}`}>
                  {busy[c.id] ? <Loader2 size={11} className="animate-spin" /> : <BadgeCheck size={11} />} Verify
                </button>
                <button onClick={() => promote(c.id)} className="text-xs px-3 py-1.5 border border-[var(--brand-accent)] text-[var(--brand-accent)] rounded-md hover:bg-[var(--brand-accent)] hover:text-white transition-colors">
                  Make Director
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {subTab === "rehearsals" && (
        <div className="space-y-3">
          <div className="text-sm text-[var(--text-secondary)]">{rehearsals.length} rehearsal{rehearsals.length !== 1 ? "s" : ""} for this parish.</div>
          {rehearsals.length === 0 ? (
            <div className="card-surface p-6 text-center text-sm text-[var(--text-secondary)]">No rehearsals scheduled. Use the Choir Hub page to schedule one.</div>
          ) : rehearsals.map((r) => (
            <div key={r.id} className="card-surface p-4 flex items-start gap-3" data-testid={`admin-rehearsal-${r.id}`}>
              <div className="flex-1">
                <div className="text-sm font-medium text-[var(--brand-primary)]">{r.title}</div>
                <div className="text-xs text-[var(--text-tertiary)] mt-0.5">{new Date(r.scheduled_at).toLocaleString()} {r.location && `· ${r.location}`}</div>
                {r.notes && <p className="text-xs text-[var(--text-secondary)] mt-1">{r.notes}</p>}
              </div>
              <button onClick={() => deleteRehearsal(r.id)} className="text-xs px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50 shrink-0">
                <Trash2 size={11} />
              </button>
            </div>
          ))}
        </div>
      )}

      {subTab === "announcements" && (
        <div className="space-y-3">
          <div className="text-sm text-[var(--text-secondary)]">{announcements.length} choir announcement{announcements.length !== 1 ? "s" : ""} for this parish.</div>
          {announcements.length === 0 ? (
            <div className="card-surface p-6 text-center text-sm text-[var(--text-secondary)]">No announcements. Post one from the Choir Hub page.</div>
          ) : announcements.map((a) => (
            <div key={a.id} className="card-surface p-4 flex items-start gap-3" data-testid={`admin-announcement-${a.id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  {a.priority === "urgent" && <span className="text-[10px] font-semibold text-red-600 uppercase">Urgent</span>}
                  <span className="text-sm font-medium text-[var(--brand-primary)]">{a.title}</span>
                </div>
                <p className="text-sm text-[var(--text-secondary)] mt-1 line-clamp-2">{a.body}</p>
                <div className="text-xs text-[var(--text-tertiary)] mt-1">by {a.author_name} · {new Date(a.created_at).toLocaleDateString()}</div>
              </div>
              <button onClick={() => deleteAnnouncement(a.id)} className="text-xs px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50 shrink-0">
                <Trash2 size={11} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Parish Bulk Import Manager ───────────────────────────────────────────
function ParishImportManager() {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);

  const downloadSample = async () => {
    try {
      const { data } = await http.get("/admin/parishes/sample");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "cpm-parishes-sample.json";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 150);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const processFile = async (file) => {
    if (!file || !file.name.endsWith(".json")) return toast.error("Please select a .json file");
    setBusy(true); setResult(null);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!Array.isArray(data)) return toast.error("File must contain a JSON array of parish objects");
      const { data: res } = await http.post("/admin/parishes/import", data);
      setResult(res);
      toast.success(`Import complete: ${res.created} created, ${res.skipped} skipped`);
    } catch (e) { toast.error(formatErr(e) || "Invalid JSON file"); }
    finally { setBusy(false); }
  };

  const onFile = (e) => processFile(e.target.files?.[0]);
  const onDrop = (e) => { e.preventDefault(); setDragging(false); processFile(e.dataTransfer.files?.[0]); };

  return (
    <div className="space-y-6">
      <div className="card-surface p-5 space-y-4">
        <h3 className="font-display text-xl text-[var(--brand-primary)] flex items-center gap-2"><Download size={16} /> Download Sample Format</h3>
        <p className="text-sm text-[var(--text-secondary)]">
          Download the sample JSON template, fill in your parishes (images via URL, coordinates optional), then upload below. All fields except <code className="bg-[var(--bg-subtle)] px-1 rounded">name</code>, <code className="bg-[var(--bg-subtle)] px-1 rounded">country</code>, and <code className="bg-[var(--bg-subtle)] px-1 rounded">city</code> are optional.
        </p>
        <button onClick={downloadSample} className="btn-primary inline-flex items-center gap-2" data-testid="parish-sample-download">
          <Download size={15} /> Download cpm-parishes-sample.json
        </button>
      </div>

      <div className="card-surface p-5 space-y-4">
        <h3 className="font-display text-xl text-[var(--brand-primary)] flex items-center gap-2"><Upload size={16} /> Upload Parish JSON</h3>
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors cursor-pointer ${
            dragging ? "border-[var(--brand-accent)] bg-amber-50" : "border-[var(--border-default)] hover:border-[var(--brand-primary)]"
          }`}
          onClick={() => document.getElementById("parish-json-upload").click()}
          data-testid="parish-drop-zone"
        >
          <Upload size={28} className="mx-auto text-[var(--text-tertiary)] mb-3" />
          <p className="text-sm font-medium text-[var(--brand-primary)]">Drop your JSON file here or click to browse</p>
          <p className="text-xs text-[var(--text-tertiary)] mt-1">Accepts .json files only</p>
          <input id="parish-json-upload" type="file" accept=".json" className="hidden" onChange={onFile} />
        </div>
        {busy && <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]"><Loader2 size={16} className="animate-spin" /> Importing parishes…</div>}
        {result && (
          <div className={`rounded-lg p-4 text-sm space-y-1 ${
            result.errors?.length ? "bg-amber-50 border border-amber-200" : "bg-emerald-50 border border-emerald-200"
          }`} data-testid="import-result">
            <div className="font-medium">{result.created} parish{result.created !== 1 ? "es" : ""} created · {result.skipped} skipped (already exist)</div>
            {result.errors?.length > 0 && (
              <div className="space-y-0.5">
                {result.errors.map((e, i) => <div key={i} className="text-amber-700 text-xs">⚠ {e}</div>)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Settings / Ranks Bulk Import ──────────────────────────────────────────
function SettingsImportManager() {
  const IMPORTABLE = [
    { key: "ccc_ranks", label: "CCC Ranks & Titles" },
    { key: "badges", label: "Recognition Badges" },
    { key: "service_types", label: "Service Team Types" },
    { key: "event_categories", label: "Event Categories" },
    { key: "prayer_categories", label: "Prayer Categories" },
  ];
  const [activeKey, setActiveKey] = useState("ccc_ranks");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const downloadSample = async () => {
    try {
      const { data } = await http.get(`/admin/settings/sample/${activeKey}`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cpm-${activeKey}-sample.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 150);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const processFile = async (file) => {
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!Array.isArray(data)) return toast.error("File must contain a JSON array of strings");
      const { data: res } = await http.post(`/admin/settings/import/${activeKey}`, data);
      setResult(res);
      toast.success(`${res.added} values added, ${res.skipped} skipped`);
    } catch (e) { toast.error(formatErr(e) || "Invalid JSON"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2">
        {IMPORTABLE.map((k) => (
          <button key={k.key} onClick={() => { setActiveKey(k.key); setResult(null); }}
            className={`px-3 py-1.5 text-sm rounded-md border ${
              activeKey === k.key ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]" : "border-[var(--border-default)]"
            }`}>{k.label}</button>
        ))}
      </div>
      <div className="card-surface p-5 space-y-4">
        <p className="text-sm text-[var(--text-secondary)]">
          The sample file is a simple JSON array of strings. Edit the values, then upload to bulk-add them to <strong>{IMPORTABLE.find(k => k.key === activeKey)?.label}</strong>. Existing values are skipped.
        </p>
        <div className="flex gap-3 flex-wrap">
          <button onClick={downloadSample} className="btn-primary inline-flex items-center gap-2">
            <Download size={15} /> Download sample
          </button>
          <label className="btn-accent inline-flex items-center gap-2 cursor-pointer">
            <Upload size={15} /> Upload JSON
            <input type="file" accept=".json" className="hidden" onChange={(e) => processFile(e.target.files?.[0])} />
          </label>
        </div>
        {busy && <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]"><Loader2 size={16} className="animate-spin" /> Importing…</div>}
        {result && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm" data-testid="settings-import-result">
            <span className="font-medium">{result.added} added</span> · {result.skipped} skipped
          </div>
        )}
      </div>
    </div>
  );
}

// ── AI Knowledge Base Manager ─────────────────────────────────────────────
function AIKnowledgeManager() {
  const [docs, setDocs] = useState([]);
  const [form, setForm] = useState({ title: "", content: "" });
  const [busy, setBusy] = useState(false);

  const load = () => http.get("/admin/ai/documents").then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.title.trim() || !form.content.trim()) return toast.error("Title and content required");
    setBusy(true);
    try {
      await http.post("/admin/ai/documents", form);
      toast.success("Document added to knowledge base");
      setForm({ title: "", content: "" }); load();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await http.delete(`/admin/ai/documents/${id}`); toast.success("Removed"); load(); } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="card-surface p-5 border-l-4 border-[var(--brand-accent)]">
        <div className="flex items-start gap-3">
          <Bot size={20} className="text-[var(--brand-accent)] mt-0.5 shrink-0" />
          <div>
            <div className="font-medium text-[var(--brand-primary)] mb-1">How the CPM Assistant learns</div>
            <p className="text-sm text-[var(--text-secondary)]">
              Paste text from the CCC Constitution, Bible lessons, church history, or any document. The AI bot reads all uploaded documents as context when answering member questions. Supports plain text — you can paste directly from a PDF.
            </p>
          </div>
        </div>
      </div>

      <div className="card-surface p-5 space-y-3">
        <h3 className="font-display text-xl text-[var(--brand-primary)] flex items-center gap-2"><Plus size={16} /> Add Knowledge Document</h3>
        <input
          className="input-clean"
          placeholder="Document title (e.g. CCC Constitution 2023, Bible Lesson: The Armour of God)"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          data-testid="ai-doc-title"
        />
        <textarea
          className="input-clean min-h-[200px] resize-y font-mono text-xs"
          placeholder="Paste the document text here… (plain text, from PDF or Word)"
          value={form.content}
          onChange={(e) => setForm({ ...form, content: e.target.value })}
          data-testid="ai-doc-content"
        />
        <div className="flex items-center justify-between">
          <span className="text-xs text-[var(--text-tertiary)]">{form.content.length.toLocaleString()} characters</span>
          <button onClick={add} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="ai-doc-add">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} Add to Knowledge Base
          </button>
        </div>
      </div>

      <div className="space-y-3">
        <div className="text-xs uppercase tracking-widest font-semibold text-[var(--text-tertiary)]">
          Knowledge Base — {docs.length} document{docs.length !== 1 ? "s" : ""}
        </div>
        {docs.length === 0 && (
          <div className="card-surface p-8 text-center">
            <BookOpen size={28} className="mx-auto text-[var(--text-tertiary)] mb-2" />
            <p className="text-sm text-[var(--text-secondary)]">No documents uploaded yet. Add the CCC Constitution or any church document above.</p>
          </div>
        )}
        {docs.map((d) => (
          <div key={d.id} className="card-surface p-4 flex items-start gap-3" data-testid={`ai-doc-${d.id}`}>
            <FileText size={16} className="text-[var(--brand-accent)] mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-[var(--brand-primary)] text-sm">{d.title}</div>
              <div className="text-xs text-[var(--text-tertiary)] mt-0.5">
                {(d.char_count || 0).toLocaleString()} chars · Added by {d.created_by_name} · {new Date(d.created_at).toLocaleDateString()}
              </div>
            </div>
            <button onClick={() => remove(d.id)} className="text-red-600 hover:text-red-800 p-1 shrink-0" data-testid={`ai-doc-delete-${d.id}`}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── CCC Hymns Import Manager ─────────────────────────────────────────────
function HymnImportManager() {
  const [raw, setRaw] = useState("");
  const [replace, setReplace] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [hymns, setHymns] = useState([]);
  const [loadingHymns, setLoadingHymns] = useState(true);
  const [filter, setFilter] = useState("");

  const loadHymns = () => {
    setLoadingHymns(true);
    http.get("/admin/ai/hymns")
      .then((r) => setHymns(r.data))
      .catch(() => {})
      .finally(() => setLoadingHymns(false));
  };
  useEffect(() => { loadHymns(); }, []);

  const parse = async () => {
    if (!raw.trim()) return toast.error("Paste hymn source text first");
    setBusy(true); setResult(null);
    try {
      const { data } = await http.post("/admin/ai/hymns/parse", { content: raw, replace });
      setResult(data);
      toast.success(`${data.parsed} hymn${data.parsed !== 1 ? "s" : ""} imported`);
      setRaw(""); loadHymns();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  const clearAll = async () => {
    if (!window.confirm("Delete all stored hymns? This cannot be undone.")) return;
    try {
      const { data } = await http.delete("/admin/ai/hymns");
      toast.success(`${data.deleted} hymn${data.deleted !== 1 ? "s" : ""} deleted`);
      setHymns([]);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const visible = filter
    ? hymns.filter((h) =>
        String(h.number).includes(filter) ||
        (h.title || "").toLowerCase().includes(filter.toLowerCase()) ||
        (h.opening_line || "").toLowerCase().includes(filter.toLowerCase())
      )
    : hymns;

  const reserved = hymns.filter((h) => h.reserved).length;

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-[var(--brand-accent)]">Knowledge Base</div>
        <h2 className="font-display text-2xl text-[var(--brand-primary)]">CCC Hymn Import</h2>
        <p className="text-sm text-[var(--text-secondary)] mt-1">
          Paste raw CCC Hymn Book text. Each hymn is parsed into a structured record that the AI uses to
          answer hymn queries accurately. Reserved hymns return a fixed reply — no invented content.
        </p>
      </div>

      {/* Format guide */}
      <div className="card-surface p-4 border-l-4 border-[var(--brand-accent)] space-y-2">
        <div className="text-xs font-semibold text-[var(--brand-primary)] uppercase tracking-wider">Expected Format</div>
        <pre className="text-xs text-[var(--text-secondary)] font-mono whitespace-pre-wrap leading-relaxed">{
`DEVOTION HYMNS          ← ALL-CAPS section header

1 (Opening)             ← number (optional category)
O Lord my God...        ← first lyric line = opening line
Chorus:
Alleluia...

2
Holy, holy, holy...

Hymns 51-100 are reserved  ← reserved range notice

51                      ← auto-marked reserved
Reserved. Information will be provided soon.`
        }</pre>
      </div>

      {/* Paste area */}
      <div className="card-surface p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl text-[var(--brand-primary)] flex items-center gap-2">
            <Upload size={15} /> Paste Hymn Source Text
          </h3>
          <span className="text-xs text-[var(--text-tertiary)]">{raw.length.toLocaleString()} chars</span>
        </div>
        <textarea
          className="input-clean min-h-[260px] resize-y font-mono text-xs"
          placeholder="Paste raw CCC hymn book text here…"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          data-testid="hymn-source-input"
        />
        <div className="flex items-center gap-4 flex-wrap">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} className="w-4 h-4" />
            <span className="text-[var(--text-secondary)]">Replace all existing hymns (full re-import)</span>
          </label>
          <button
            onClick={parse}
            disabled={busy || !raw.trim()}
            className="btn-primary inline-flex items-center gap-2 ml-auto"
            data-testid="hymn-parse-btn"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <BookOpen size={14} />} Parse & Import
          </button>
        </div>
        {result && (
          <div className="rounded-lg p-3 bg-emerald-50 border border-emerald-200 text-sm text-emerald-800">
            ✓ {result.parsed} hymn{result.parsed !== 1 ? "s" : ""} imported{result.replaced ? " (all previous hymns replaced)" : " (upserted by number)"}.
          </div>
        )}
      </div>

      {/* Stored hymns */}
      <div className="space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-sm font-semibold text-[var(--brand-primary)] flex-1">
            {hymns.length} hymn{hymns.length !== 1 ? "s" : ""} stored
            {reserved > 0 && <span className="ml-2 text-xs text-amber-600 font-normal">· {reserved} reserved</span>}
          </div>
          <input
            value={filter} onChange={(e) => setFilter(e.target.value)}
            placeholder="Search by number or title…"
            className="input-clean text-sm w-52"
          />
          <button onClick={loadHymns} className="text-xs text-[var(--text-tertiary)] hover:text-[var(--brand-accent)] inline-flex items-center gap-1">
            <RefreshCw size={11} /> Refresh
          </button>
          {hymns.length > 0 && (
            <button onClick={clearAll} className="text-xs text-red-500 hover:text-red-700 inline-flex items-center gap-1">
              <Trash2 size={11} /> Clear all
            </button>
          )}
        </div>

        {loadingHymns ? (
          <div className="space-y-1 animate-pulse">
            {[1, 2, 3].map((n) => <div key={n} className="card-surface h-12 rounded-lg" />)}
          </div>
        ) : visible.length === 0 ? (
          <div className="card-surface p-8 text-center">
            <BookOpen size={24} className="mx-auto text-[var(--text-tertiary)] mb-2" />
            <p className="text-sm text-[var(--text-secondary)]">No hymns stored yet. Paste hymn source text above and import.</p>
          </div>
        ) : (
          <div className="divide-y divide-[var(--border-default)] border border-[var(--border-default)] rounded-xl overflow-hidden">
            {visible.slice(0, 200).map((h) => (
              <div key={h.number} className={`flex items-start gap-3 px-4 py-3 text-sm ${
                h.reserved ? "bg-amber-50" : "bg-[var(--bg-paper)]"
              }`} data-testid={`hymn-row-${h.number}`}>
                <div className="w-10 shrink-0 text-right font-mono text-[var(--brand-primary)] font-semibold">{h.number}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[var(--text-primary)] truncate">{h.title || h.opening_line || "(no title)"}</div>
                  <div className="text-xs text-[var(--text-tertiary)] truncate">
                    {h.section && <span>{h.section}</span>}
                    {h.category && <span> · {h.category}</span>}
                  </div>
                </div>
                {h.reserved && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium shrink-0">Reserved</span>
                )}
              </div>
            ))}
            {visible.length > 200 && (
              <div className="px-4 py-2 text-xs text-[var(--text-tertiary)] text-center">
                {visible.length - 200} more hymns not shown — use search to filter
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Daily Posts Manager ───────────────────────────────────────────────────
function DailyPostsManager() {
  const [enabled, setEnabled] = useState("");
  const [triggering, setTriggering] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    http.get("/integrations").then((r) => {
      const item = r.data.find((i) => i.label === "daily_posts_enabled");
      setEnabled(item?.meta?.value || "false");
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await http.post("/integrations", { label: "daily_posts_enabled", value: enabled });
      toast.success(`Daily posts ${enabled === "true" ? "enabled" : "disabled"}`);
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  };

  const trigger = async () => {
    setTriggering(true);
    try {
      await http.post("/admin/daily-posts/trigger", {});
      toast.success("Daily posts generation started! Check the Global Feed in a moment.");
    } catch (e) { toast.error(formatErr(e)); } finally { setTriggering(false); }
  };

  const POST_TYPES = [
    { emoji: "✝️", label: "Daily Devotion", desc: "A short spiritual devotion with a Bible verse reference" },
    { emoji: "🙏", label: "Morning Prayer", desc: "A community prayer for members to pray together" },
    { emoji: "📖", label: "Verse of the Day", desc: "A Bible verse with brief CCC-centred reflection" },
    { emoji: "🎵", label: "Music for the Day", desc: "A suggested CCC hymn or spiritual song" },
  ];

  return (
    <div className="space-y-6">
      <div className="card-surface p-5 border-l-4 border-[var(--brand-accent)]">
        <div className="flex items-start gap-3">
          <Sparkles size={20} className="text-[var(--brand-accent)] mt-0.5 shrink-0" />
          <div>
            <div className="font-medium text-[var(--brand-primary)] mb-1">AI-Generated Daily Posts</div>
            <p className="text-sm text-[var(--text-secondary)]">
              When enabled, the server automatically generates 4 posts to the Global Feed every day at <strong>6:00 AM UTC</strong> — a devotion, prayer, Bible verse, and music suggestion — all tailored to CCC worship and culture. Requires an OpenAI API key in Integrations.
            </p>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {POST_TYPES.map((p) => (
          <div key={p.label} className="card-surface p-4 text-center space-y-2">
            <div className="text-3xl">{p.emoji}</div>
            <div className="font-medium text-sm text-[var(--brand-primary)]">{p.label}</div>
            <div className="text-xs text-[var(--text-secondary)]">{p.desc}</div>
          </div>
        ))}
      </div>

      <div className="card-surface p-5 space-y-4">
        <h3 className="font-display text-xl text-[var(--brand-primary)]">Configuration</h3>
        <div className="flex items-center gap-4 flex-wrap">
          <label className="text-sm font-medium text-[var(--brand-primary)]">Daily Posts:</label>
          <div className="flex gap-2">
            <button
              onClick={() => setEnabled("true")}
              className={`px-4 py-2 rounded-md border text-sm transition-colors ${
                enabled === "true" ? "bg-emerald-600 text-white border-emerald-600" : "border-[var(--border-default)] text-[var(--text-secondary)]"
              }`}
            >Enable</button>
            <button
              onClick={() => setEnabled("false")}
              className={`px-4 py-2 rounded-md border text-sm transition-colors ${
                enabled === "false" ? "bg-red-600 text-white border-red-600" : "border-[var(--border-default)] text-[var(--text-secondary)]"
              }`}
            >Disable</button>
          </div>
          <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2">
            {saving ? <Loader2 size={14} className="animate-spin" /> : null} Save
          </button>
        </div>
        <div className="border-t border-[var(--border-default)] pt-4">
          <div className="text-sm font-medium text-[var(--brand-primary)] mb-2">Manual Trigger</div>
          <p className="text-xs text-[var(--text-secondary)] mb-3">Fire today's posts right now (skips any already posted today). Useful for testing or if the server missed a scheduled run.</p>
          <button onClick={trigger} disabled={triggering} className="btn-accent inline-flex items-center gap-2" data-testid="trigger-daily-posts">
            {triggering ? <Loader2 size={15} className="animate-spin" /> : <PlayCircle size={15} />}
            {triggering ? "Generating…" : "Trigger Daily Posts Now"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── CPM Wave Tracks Manager ───────────────────────────────────────────────────────────────
const TRACK_CATEGORIES = ["hymn", "praise", "devotion", "choir", "oldies", "worship"];

function CPMWaveManager() {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", artist: "", url: "", category: "hymn", description: "", featured: false });
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    http.get("/admin/cpmwave/tracks")
      .then((r) => setTracks(r.data))
      .catch(() => toast.error("Could not load tracks"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.title.trim() || !form.url.trim()) { toast.error("Title and URL are required"); return; }
    setSaving(true);
    try {
      const { data } = await http.post("/admin/cpmwave/tracks", form);
      setTracks([data, ...tracks]);
      setForm({ title: "", artist: "", url: "", category: "hymn", description: "", featured: false });
      toast.success("Track added");
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this track?")) return;
    try {
      await http.delete(`/admin/cpmwave/tracks/${id}`);
      setTracks(tracks.filter((t) => t.id !== id));
      toast.success("Removed");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const toggleFeatured = async (track) => {
    try {
      await http.patch(`/admin/cpmwave/tracks/${track.id}`, { featured: !track.featured });
      setTracks(tracks.map((t) => t.id === track.id ? { ...t, featured: !t.featured } : t));
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-[var(--brand-accent)]">CPM Wave</div>
        <h2 className="font-display text-2xl text-[var(--brand-primary)]">Music Library</h2>
        <p className="text-sm text-[var(--text-secondary)] mt-1">
          Add tracks from <a href="https://cpmwave.com" target="_blank" rel="noopener noreferrer" className="text-[var(--brand-accent)] hover:underline inline-flex items-center gap-0.5">cpmwave.com <ExternalLink size={11} /></a>. The AI will share these randomly in daily music posts and members can browse them in the Music tab.
        </p>
      </div>

      {/* Add track form */}
      <div className="card-surface p-5 space-y-4">
        <div className="text-sm font-semibold text-[var(--brand-primary)]">Add a Track</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-[var(--text-tertiary)] block mb-1">Song Title *</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Oluwa Po Loore" className="input-clean text-sm w-full" />
          </div>
          <div>
            <label className="text-xs text-[var(--text-tertiary)] block mb-1">Artist / Choir</label>
            <input value={form.artist} onChange={(e) => setForm({ ...form, artist: e.target.value })}
              placeholder="e.g. CCC Makoko Choir" className="input-clean text-sm w-full" />
          </div>
        </div>
        <div>
          <label className="text-xs text-[var(--text-tertiary)] block mb-1">CPM Wave URL *</label>
          <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
            placeholder="https://cpmwave.com/music/track/..." className="input-clean text-sm w-full font-mono" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-[var(--text-tertiary)] block mb-1">Category</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input-clean text-sm w-full">
              {TRACK_CATEGORIES.map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="w-4 h-4" />
              <span className="text-[var(--text-secondary)]">Feature on Music Discover page</span>
            </label>
          </div>
        </div>
        <div>
          <label className="text-xs text-[var(--text-tertiary)] block mb-1">Description (optional)</label>
          <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Brief note about the track" className="input-clean text-sm w-full" />
        </div>
        <button onClick={add} disabled={saving || !form.title.trim() || !form.url.trim()}
          className="btn-primary text-sm inline-flex items-center gap-2">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add Track
        </button>
      </div>

      {/* Shortcut examples */}
      <div className="card-surface p-4 space-y-2">
        <div className="text-xs font-semibold text-[var(--text-tertiary)] uppercase tracking-wider">Paste these URLs to get started</div>
        {[
          { title: "Oluwa Po Loore", artist: "CCC Makoko Choir", url: "https://cpmwave.com/music/track/oluwa-po-loore-by-ccc-makoko" },
          { title: "Ijo Mimo", artist: "Seyi Solagbade", url: "https://cpmwave.com/music/track/seyi_solagbade-ijo_mimo-ijo_mimo" },
          { title: "Celestial Oldies", artist: "HOD", url: "https://cpmwave.com/music/track/celestial-oldies" },
          { title: "Moti Moyin Tele Oluwa Olugbala", artist: "Bro Bro (Steve Pelemo)", url: "https://cpmwave.com/music/track/bro_bro_-_steve_pelemo-moti_moyin_tele-oluwa_olugbala" },
        ].map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            <button onClick={() => setForm({ ...form, title: s.title, artist: s.artist, url: s.url })}
              className="text-xs px-2.5 py-1 rounded border border-[var(--brand-accent)] text-[var(--brand-accent)] hover:bg-amber-50 transition-colors">
              Use
            </button>
            <span className="text-xs text-[var(--text-primary)] font-medium">{s.title}</span>
            <span className="text-xs text-[var(--text-tertiary)]">{s.artist}</span>
            <a href={s.url} target="_blank" rel="noopener noreferrer" className="ml-auto text-xs text-[var(--text-tertiary)] hover:text-[var(--brand-accent)]"><ExternalLink size={11} /></a>
          </div>
        ))}
      </div>

      {/* Track list */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-[var(--brand-primary)]">{tracks.length} track{tracks.length !== 1 ? "s" : ""} in library</div>
          <button onClick={load} className="text-xs text-[var(--text-tertiary)] hover:text-[var(--brand-accent)] inline-flex items-center gap-1">
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
        {loading ? (
          <div className="space-y-2 animate-pulse">{[1, 2, 3].map((n) => <div key={n} className="card-surface h-16 rounded-xl" />)}</div>
        ) : tracks.length === 0 ? (
          <div className="card-surface p-8 text-center text-sm text-[var(--text-secondary)]">
            No tracks yet. Add the first one above.
          </div>
        ) : (
          tracks.map((t) => (
            <div key={t.id} className="card-surface p-4 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-[var(--brand-primary)] truncate">{t.title}</span>
                  {t.featured && <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium shrink-0">Featured</span>}
                  <span className="text-xs px-1.5 py-0.5 rounded bg-[var(--bg-subtle)] text-[var(--text-tertiary)] capitalize shrink-0">{t.category}</span>
                </div>
                {t.artist && <div className="text-xs text-[var(--text-tertiary)] truncate mt-0.5">{t.artist}</div>}
                <a href={t.url} target="_blank" rel="noopener noreferrer"
                  className="text-xs text-[var(--brand-accent)] hover:underline inline-flex items-center gap-0.5 mt-0.5 truncate max-w-xs">
                  {t.url.replace("https://", "")} <ExternalLink size={10} />
                </a>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => toggleFeatured(t)} title={t.featured ? "Unfeature" : "Feature"}
                  className={`p-1.5 rounded hover:bg-[var(--bg-subtle)] transition-colors ${t.featured ? "text-amber-500" : "text-[var(--text-tertiary)]"}`}>
                  <Star size={14} fill={t.featured ? "currentColor" : "none"} />
                </button>
                <button onClick={() => remove(t.id)}
                  className="p-1.5 rounded hover:bg-[var(--bg-subtle)] text-red-400 hover:text-red-600">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ── ContestsManager ────────────────────────────────────────────────────────
const TYPE_OPTS = [
  { v: "photo",     l: "📸 Photo Contest" },
  { v: "video",     l: "🎬 Video Contest" },
  { v: "verse",     l: "📖 Memory Verse" },
  { v: "testimony", l: "✨ Testimony" },
];
const BLANK_CONTEST = { title: "", description: "", type: "photo", prize: "", start_at: "", end_at: "" };

function ContestsManager() {
  const [contests, setContests] = useState([]);
  const [form, setForm] = useState(BLANK_CONTEST);
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [entries, setEntries] = useState({});

  const load = () => http.get("/contests").then((r) => setContests(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title || !form.start_at || !form.end_at) return toast.error("Title, start and end dates are required.");
    setCreating(true);
    try { await http.post("/contests", form); toast.success("Contest created!"); setForm(BLANK_CONTEST); load(); }
    catch (e) { toast.error(formatErr(e)); } finally { setCreating(false); }
  };

  const remove = async (cid, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try { await http.delete(`/contests/${cid}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatErr(e)); }
  };

  const toggleStatus = async (c) => {
    const next = c.status === "active" ? "closed" : "active";
    try { await http.patch(`/contests/${c.id}`, { status: next }); toast.success(`Contest ${next}`); load(); } catch (e) { toast.error(formatErr(e)); }
  };

  const loadEntries = async (cid) => {
    if (entries[cid]) { setExpanded(expanded === cid ? null : cid); return; }
    try {
      const { data } = await http.get(`/contests/${cid}/entries`);
      setEntries((p) => ({ ...p, [cid]: data }));
      setExpanded(cid);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const declareWinner = async (cid, eid, entryUserName) => {
    if (!window.confirm(`Declare ${entryUserName} as winner?`)) return;
    try { await http.post(`/contests/${cid}/winner/${eid}`); toast.success("Winner declared!"); load(); setEntries((p) => ({ ...p, [cid]: undefined })); setExpanded(null); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const STATUS_COLOR = { active: "bg-emerald-50 text-emerald-700 border-emerald-200", ended: "bg-gray-100 text-gray-500 border-gray-200", closed: "bg-amber-50 text-amber-700 border-amber-200" };

  return (
    <div className="space-y-6">
      {/* Create form */}
      <div className="card-surface p-5">
        <h3 className="font-display text-xl text-[var(--brand-primary)] mb-4 flex items-center gap-2"><Trophy size={16} /> Create Contest</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Contest Title *</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-clean w-full text-sm" placeholder="e.g. Juvenile Harvest Photo Contest 2026" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Description *</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="input-clean w-full text-sm resize-none" placeholder="Describe what members should submit and how voting works…" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Type</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="input-clean w-full text-sm">
              {TYPE_OPTS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Prize / Award</label>
            <input value={form.prize} onChange={(e) => setForm({ ...form, prize: e.target.value })} className="input-clean w-full text-sm" placeholder="e.g. Gift card, Featured as CPM Star" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Start Date *</label>
            <input type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} className="input-clean w-full text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">End Date *</label>
            <input type="datetime-local" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} className="input-clean w-full text-sm" />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={create} disabled={creating} className="btn-primary inline-flex items-center gap-2">
            {creating && <Loader2 size={14} className="animate-spin" />} Create Contest
          </button>
        </div>
      </div>

      {/* Contest list */}
      <div className="space-y-3">
        {contests.length === 0 && <div className="card-surface p-6 text-sm text-[var(--text-secondary)] text-center">No contests created yet.</div>}
        {contests.map((c) => (
          <div key={c.id} className="card-surface overflow-hidden">
            <div className="p-4 flex flex-wrap items-start gap-3">
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-[var(--brand-primary)]">{c.title}</span>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border font-semibold ${STATUS_COLOR[c.status] || STATUS_COLOR.ended}`}>{c.status}</span>
                  <span className="text-[10px] text-[var(--text-tertiary)] uppercase">{TYPE_OPTS.find((t) => t.v === c.type)?.l}</span>
                </div>
                <div className="text-xs text-[var(--text-tertiary)] mt-0.5">{c.prize && `🏆 ${c.prize} · `}Ends {new Date(c.end_at).toLocaleDateString()}</div>
                {c.winner_entry_id && <div className="text-xs text-emerald-600 mt-0.5 font-semibold">✓ Winner declared</div>}
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => loadEntries(c.id)} className="text-xs px-3 py-1.5 rounded-md border border-[var(--border-default)] hover:border-[var(--brand-primary)] inline-flex items-center gap-1">
                  <Users size={12} /> Entries {expanded === c.id ? "▲" : "▼"}
                </button>
                {c.status === "active" && (
                  <button onClick={() => toggleStatus(c)} className="text-xs px-3 py-1.5 rounded-md border border-amber-300 text-amber-700 hover:bg-amber-50">Close</button>
                )}
                <button onClick={() => remove(c.id, c.title)} className="text-xs px-3 py-1.5 rounded-md border border-red-200 text-red-700 hover:bg-red-50"><Trash2 size={12} /></button>
              </div>
            </div>
            {expanded === c.id && entries[c.id] && (
              <div className="border-t border-[var(--border-default)] bg-[var(--bg-subtle)] p-4">
                {entries[c.id].length === 0 ? (
                  <p className="text-sm text-[var(--text-secondary)]">No entries yet.</p>
                ) : (
                  <div className="space-y-3">
                    {entries[c.id].map((e, i) => (
                      <div key={e.id} className={`flex items-start gap-3 p-3 rounded-xl bg-[var(--bg-paper)] border ${e.id === c.winner_entry_id ? "border-[var(--brand-accent)]" : "border-[var(--border-default)]"}`}>
                        <span className="font-display text-xl text-[var(--border-default)] shrink-0 w-6">#{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-[var(--brand-primary)]">{e.user_name}</span>
                            <span className="text-xs text-[var(--brand-accent)] font-semibold">{e.votes} votes</span>
                            {e.id === c.winner_entry_id && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--brand-accent)] text-white font-bold">WINNER</span>}
                          </div>
                          {e.body && <p className="text-xs text-[var(--text-secondary)] mt-0.5 line-clamp-2">{e.body}</p>}
                          {e.media_urls?.length > 0 && <div className="text-xs text-[var(--text-tertiary)] mt-0.5">{e.media_urls.length} media file(s)</div>}
                        </div>
                        {c.status === "active" && !c.winner_entry_id && (
                          <button onClick={() => declareWinner(c.id, e.id, e.user_name)} className="text-xs px-3 py-1.5 rounded-md border border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-semibold shrink-0">
                            <Trophy size={12} className="inline mr-1" />Winner
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── CpmStarsManager ─────────────────────────────────────────────────────────
const BLANK_STAR = { member_name: "", member_id: "", photo_url: "", award: "", description: "", period: "week", period_label: "", expires_at: "" };

function CpmStarsManager() {
  const [stars, setStars] = useState([]);
  const [allStars, setAllStars] = useState([]);
  const [form, setForm] = useState(BLANK_STAR);
  const [creating, setCreating] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const load = () => http.get("/cpm-stars", { params: { active_only: false } }).then((r) => { setAllStars(r.data); setStars(r.data.filter((s) => s.active)); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.member_name || !form.award || !form.period_label) return toast.error("Member name, award, and period label are required.");
    setCreating(true);
    try { await http.post("/cpm-stars", form); toast.success("CPM Star created!"); setForm(BLANK_STAR); load(); }
    catch (e) { toast.error(formatErr(e)); } finally { setCreating(false); }
  };

  const toggle = async (s) => {
    try { await http.patch(`/cpm-stars/${s.id}`, { active: !s.active }); toast.success(s.active ? "Star deactivated" : "Star activated"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (sid, name) => {
    if (!window.confirm(`Remove "${name}" from CPM Stars?`)) return;
    try { await http.delete(`/cpm-stars/${sid}`); toast.success("Removed"); load(); } catch (e) { toast.error(formatErr(e)); }
  };

  const displayed = showAll ? allStars : stars;

  return (
    <div className="space-y-6">
      {/* Info */}
      <div className="flex items-start gap-3 p-4 rounded-xl bg-[var(--brand-accent)]/8 border border-[var(--brand-accent)]/30">
        <Crown size={16} className="text-[var(--brand-accent)] shrink-0 mt-0.5" />
        <div className="text-sm text-[var(--text-secondary)] leading-relaxed">
          <span className="font-semibold text-[var(--brand-primary)]">CPM Stars</span> are featured members displayed prominently on the home page and global feed. Use them to celebrate contest winners, faithful members, and community champions.
        </div>
      </div>

      {/* Create form */}
      <div className="card-surface p-5">
        <h3 className="font-display text-xl text-[var(--brand-primary)] mb-4 flex items-center gap-2"><Star size={16} /> Feature a CPM Star</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Member Name *</label>
            <input value={form.member_name} onChange={(e) => setForm({ ...form, member_name: e.target.value })} className="input-clean w-full text-sm" placeholder="Full name" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Award Title *</label>
            <input value={form.award} onChange={(e) => setForm({ ...form, award: e.target.value })} className="input-clean w-full text-sm" placeholder="e.g. Most Active Member, Prayer Warrior" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Photo URL</label>
            <input value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} className="input-clean w-full text-sm" placeholder="https://… (upload via media manager first)" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="input-clean w-full text-sm resize-none" placeholder="Why are they featured? What did they do?" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Period</label>
            <select value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} className="input-clean w-full text-sm">
              <option value="week">Star of the Week</option>
              <option value="month">Star of the Month</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Period Label *</label>
            <input value={form.period_label} onChange={(e) => setForm({ ...form, period_label: e.target.value })} className="input-clean w-full text-sm" placeholder="e.g. Week of 12 May 2026" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Expires At (optional)</label>
            <input type="datetime-local" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="input-clean w-full text-sm" />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={create} disabled={creating} className="btn-primary inline-flex items-center gap-2">
            {creating && <Loader2 size={14} className="animate-spin" />}<Crown size={14} /> Feature Star
          </button>
        </div>
      </div>

      {/* Stars list */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-[var(--text-secondary)]">{stars.length} active star{stars.length !== 1 ? "s" : ""}</span>
          <button onClick={() => setShowAll(!showAll)} className="text-xs text-[var(--brand-accent)] hover:underline">{showAll ? "Active only" : `Show all (${allStars.length})`}</button>
        </div>
        {displayed.length === 0 && <div className="card-surface p-6 text-sm text-[var(--text-secondary)] text-center">No CPM Stars yet — feature your first member above!</div>}
        {displayed.map((s) => (
          <div key={s.id} className={`card-surface p-4 flex items-start gap-4 ${!s.active ? "opacity-60" : ""}`}>
            <div className="w-14 h-14 rounded-2xl overflow-hidden bg-[var(--brand-primary)] text-white grid place-items-center text-xl font-display shrink-0 ring-2 ring-[var(--brand-accent)]/30">
              {s.photo_url ? <img src={s.photo_url} alt={s.member_name} className="w-full h-full object-cover" /> : s.member_name?.[0]}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-[var(--brand-primary)]">{s.member_name}</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--brand-accent)]/15 text-[var(--brand-accent)] font-bold border border-[var(--brand-accent)]/30">
                  {s.period === "week" ? "⭐ Star of the Week" : "🌟 Star of the Month"}
                </span>
                {!s.active && <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200">Inactive</span>}
              </div>
              <div className="text-xs text-[var(--brand-accent)] font-semibold mt-0.5">{s.award}</div>
              <div className="text-xs text-[var(--text-tertiary)] mt-0.5">{s.period_label}</div>
              {s.description && <div className="text-xs text-[var(--text-secondary)] mt-1 line-clamp-2">{s.description}</div>}
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => toggle(s)} className={`text-xs px-3 py-1.5 rounded-md border font-semibold ${s.active ? "border-amber-300 text-amber-700 hover:bg-amber-50" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}`}>
                {s.active ? "Deactivate" : "Activate"}
              </button>
              <button onClick={() => remove(s.id, s.member_name)} className="text-xs px-3 py-1.5 rounded-md border border-red-200 text-red-700 hover:bg-red-50"><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CELESTIAL MATCH ADMIN
// ═══════════════════════════════════════════════════════════════════════════════

const CONNECT_REPORT_ACTIONS = [
  { value: "dismiss", label: "Dismiss" },
  { value: "warn_user", label: "Warn User" },
  { value: "freeze", label: "Freeze Account" },
  { value: "ban", label: "Ban Account" },
];

const KEYWORD_CATEGORIES = ["money", "scam", "explicit", "threat", "offplatform", "custom"];
const KEYWORD_ACTIONS = ["warn", "soft_block", "flag", "hard_block"];
const VERIFY_LEVELS = ["profile", "parish", "identity", "trusted"];
const MATCH_ACTIONS = ["flag", "clear", "end"];

function ConnectSettingsPanel() {
  const BOOL_KEYS = [
    ["enabled", "Feature Enabled"],
    ["friendship_mode_enabled", "Friendship Mode"],
    ["dating_mode_enabled", "Dating / Relationship Mode"],
    ["match_expiry_enabled", "Auto-expire inactive matches"],
    ["ai_moderation_enabled", "AI-assisted Moderation"],
    ["verification_required", "Require verification badge to access"],
    ["testimony_enabled", "Allow testimonies"],
  ];
  const NUM_KEYS = [
    ["daily_swipe_limit", "Daily Swipe Limit"],
    ["daily_like_limit", "Daily Like Limit"],
    ["max_active_matches", "Max Active Matches"],
    ["inactivity_days_reminder", "Inactivity Reminder (days)"],
    ["inactivity_days_expire", "Inactivity Expiry (days)"],
  ];
  const SENS_KEY = "ai_moderation_sensitivity";

  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    http.get("/admin/connect/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await http.put("/admin/connect/settings", settings);
      toast.success("Connect settings saved");
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  if (!settings) return <div className="flex justify-center py-8"><Loader2 size={24} className="animate-spin text-[var(--brand-accent)]" /></div>;

  return (
    <div className="space-y-5 max-w-xl">
      <div className="card-surface p-5 space-y-3">
        <div className="text-xs font-semibold uppercase tracking-widest text-[var(--text-tertiary)]">Feature Toggles</div>
        {BOOL_KEYS.map(([k, label]) => (
          <label key={k} className="flex items-center justify-between text-sm cursor-pointer">
            <span>{label}</span>
            <input type="checkbox" checked={!!settings[k]} onChange={(e) => setSettings((s) => ({ ...s, [k]: e.target.checked }))} className="accent-[var(--brand-primary)] w-4 h-4" />
          </label>
        ))}
      </div>
      <div className="card-surface p-5 space-y-3">
        <div className="text-xs font-semibold uppercase tracking-widest text-[var(--text-tertiary)]">Limits & Lifecycle</div>
        {NUM_KEYS.map(([k, label]) => (
          <label key={k} className="text-sm block">
            {label}
            <input type="number" min={1} max={9999} className="input-clean mt-1 w-full" value={settings[k] ?? ""} onChange={(e) => setSettings((s) => ({ ...s, [k]: parseInt(e.target.value) || 0 }))} />
          </label>
        ))}
        <label className="text-sm block">
          AI Moderation Sensitivity
          <select className="input-clean mt-1 w-full" value={settings[SENS_KEY] || "medium"} onChange={(e) => setSettings((s) => ({ ...s, [SENS_KEY]: e.target.value }))}>
            <option value="low">Low</option>
            <option value="medium">Medium (recommended)</option>
            <option value="high">High</option>
          </select>
        </label>
      </div>
      <div className="card-surface p-5 space-y-3">
        <div className="text-xs font-semibold uppercase tracking-widest text-[var(--text-tertiary)]">Safety Banner Text</div>
        <textarea className="input-clean w-full min-h-[80px] resize-none text-sm" value={settings.safety_banner_text || ""} onChange={(e) => setSettings((s) => ({ ...s, safety_banner_text: e.target.value }))} />
      </div>
      <button onClick={save} disabled={busy} className="btn-primary inline-flex items-center gap-2 w-full justify-center py-3">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <BadgeCheck size={15} />} Save Settings
      </button>
    </div>
  );
}

function ConnectKeywordsPanel() {
  const [keywords, setKeywords] = useState([]);
  const [form, setForm] = useState({ category: "money", keyword: "", action: "hard_block", active: true });
  const [busy, setBusy] = useState(false);

  const load = () => http.get("/admin/connect/keywords").then((r) => setKeywords(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.keyword.trim()) { toast.error("Enter a keyword"); return; }
    setBusy(true);
    try { await http.post("/admin/connect/keywords", form); toast.success("Keyword added"); setForm((f) => ({ ...f, keyword: "" })); load(); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await http.delete(`/admin/connect/keywords/${id}`); load(); } catch (e) { toast.error(formatErr(e)); }
  };

  const actionBadge = { warn: "bg-amber-100 text-amber-700", soft_block: "bg-orange-100 text-orange-700", flag: "bg-red-100 text-red-700", hard_block: "bg-red-700 text-white" };

  return (
    <div className="space-y-4 max-w-xl">
      <div className="card-surface p-5 space-y-3">
        <div className="text-xs font-semibold uppercase tracking-widest text-[var(--text-tertiary)]">Add Keyword Rule</div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm block">
            Category
            <select className="input-clean mt-1 w-full" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
              {KEYWORD_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="text-sm block">
            Action
            <select className="input-clean mt-1 w-full" value={form.action} onChange={(e) => setForm((f) => ({ ...f, action: e.target.value }))}>
              {KEYWORD_ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </label>
        </div>
        <label className="text-sm block">
          Keyword / Phrase
          <input className="input-clean mt-1 w-full" value={form.keyword} onChange={(e) => setForm((f) => ({ ...f, keyword: e.target.value }))} placeholder="e.g. wire transfer" />
        </label>
        <button onClick={add} disabled={busy} className="btn-primary inline-flex items-center gap-2 text-sm">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Add Rule
        </button>
      </div>
      <div className="space-y-2">
        <div className="text-xs text-[var(--text-tertiary)]">{keywords.length} custom rules (built-in rules also apply)</div>
        {keywords.map((kw) => (
          <div key={kw.id} className="card-surface p-3 flex items-center gap-3">
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">{kw.category}</span>
            <span className="flex-1 font-mono text-sm text-[var(--brand-primary)]">{kw.keyword}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${actionBadge[kw.action] || "bg-gray-100 text-gray-600"}`}>{kw.action}</span>
            <button onClick={() => remove(kw.id)} className="text-red-400 hover:text-red-600 p-1"><Trash2 size={13} /></button>
          </div>
        ))}
        {keywords.length === 0 && <div className="text-sm text-[var(--text-tertiary)] card-surface p-4 text-center">No custom keyword rules yet. Built-in rules (money, scams, explicit, threats) always apply.</div>}
      </div>
    </div>
  );
}

function ConnectReportsPanel() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("open");
  const [actionNote, setActionNote] = useState({});
  const [resolving, setResolving] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    http.get("/admin/connect/reports", { params: statusFilter ? { status: statusFilter } : {} })
      .then((r) => setReports(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (rid, action) => {
    setResolving((s) => ({ ...s, [rid]: true }));
    try {
      await http.post(`/admin/connect/reports/${rid}/resolve`, { action, note: actionNote[rid] || "" });
      toast.success("Report resolved");
      load();
    } catch (e) { toast.error(formatErr(e)); } finally { setResolving((s) => ({ ...s, [rid]: false })); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        {["open", "resolved", ""].map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)} className={`text-xs px-3 py-1.5 rounded-full border ${statusFilter === s ? "bg-[var(--brand-primary)] text-white" : "border-[var(--border-default)] text-[var(--text-secondary)]"}`}>
            {s === "" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>
      {loading ? <div className="flex justify-center py-8"><Loader2 size={24} className="animate-spin text-[var(--brand-accent)]" /></div> : reports.length === 0 ? (
        <div className="card-surface p-8 text-center text-sm text-[var(--text-secondary)]">No reports found</div>
      ) : reports.map((r) => (
        <div key={r.id} className="card-surface p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-medium text-sm text-[var(--brand-primary)]">{r.reason}</div>
              <div className="text-xs text-[var(--text-tertiary)] mt-0.5">
                Reporter: {r.reporter_name || r.reporter_id} · {new Date(r.created_at).toLocaleString()}
              </div>
              {r.target_user && <div className="text-xs mt-1"><span className="font-medium">Reported:</span> {r.target_user.name} ({r.target_user.email})</div>}
              {r.note && <div className="text-xs text-[var(--text-secondary)] mt-1 italic">"{r.note}"</div>}
            </div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border shrink-0 ${r.status === "open" ? "bg-red-100 text-red-700 border-red-200" : "bg-gray-100 text-gray-600 border-gray-200"}`}>{r.status}</span>
          </div>
          {r.status === "open" && (
            <div className="flex flex-wrap gap-2 items-end">
              <input className="input-clean flex-1 text-sm" value={actionNote[r.id] || ""} onChange={(e) => setActionNote((s) => ({ ...s, [r.id]: e.target.value }))} placeholder="Note (optional)" />
              {CONNECT_REPORT_ACTIONS.map(({ value, label }) => (
                <button key={value} disabled={resolving[r.id]} onClick={() => resolve(r.id, value)} className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors ${value === "dismiss" ? "border-gray-200 text-gray-600 hover:bg-gray-50" : value === "warn_user" ? "border-amber-300 text-amber-700 hover:bg-amber-50" : value === "freeze" ? "border-orange-300 text-orange-700 hover:bg-orange-50" : "border-red-300 text-red-700 hover:bg-red-50"}`}>
                  {resolving[r.id] && <Loader2 size={11} className="animate-spin inline mr-1" />}{label}
                </button>
              ))}
            </div>
          )}
          {r.status === "resolved" && (
            <div className="text-xs text-[var(--text-tertiary)]">Resolved: {r.resolved_action} · {r.resolve_note}</div>
          )}
        </div>
      ))}
    </div>
  );
}

function ConnectFlaggedMessagesPanel() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    http.get("/admin/connect/flagged-messages").then((r) => setMessages(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const statusBadge = { warn: "bg-amber-100 text-amber-700", soft_block: "bg-orange-100 text-orange-700", flag: "bg-red-100 text-red-700" };

  return (
    <div className="space-y-3">
      <div className="text-xs text-[var(--text-tertiary)]">{messages.length} flagged / warned messages</div>
      {loading ? <div className="flex justify-center py-8"><Loader2 size={24} className="animate-spin text-[var(--brand-accent)]" /></div> : messages.length === 0 ? (
        <div className="card-surface p-8 text-center text-sm text-[var(--text-secondary)]">No flagged messages</div>
      ) : messages.map((msg) => (
        <div key={msg.id} className="card-surface p-4 space-y-2">
          <div className="flex items-center gap-2">
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${statusBadge[msg.moderation_status] || "bg-gray-100 text-gray-600"}`}>{msg.moderation_status}</span>
            <span className="text-xs text-[var(--text-tertiary)]">From: {msg.sender?.name || msg.sender_id} · {new Date(msg.created_at).toLocaleString()}</span>
          </div>
          <p className="text-sm text-[var(--text-primary)] bg-[var(--bg-subtle)] rounded-lg px-3 py-2">{msg.body}</p>
          {msg.moderation_flags?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {msg.moderation_flags.map((f, i) => <span key={i} className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-100">{f.type || f.keyword}</span>)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ConnectProfilesPanel() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [frozenOnly, setFrozenOnly] = useState(false);
  const [verifying, setVerifying] = useState({});
  const [freezing, setFreezing] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    http.get("/admin/connect/profiles", { params: frozenOnly ? { frozen: true } : {} })
      .then((r) => setProfiles(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [frozenOnly]);

  useEffect(() => { load(); }, [load]);

  const verify = async (uid, level) => {
    setVerifying((s) => ({ ...s, [uid]: true }));
    try { await http.post(`/admin/connect/profiles/${uid}/verify`, { level }); toast.success("Verification updated"); load(); } catch (e) { toast.error(formatErr(e)); } finally { setVerifying((s) => ({ ...s, [uid]: false })); }
  };

  const freeze = async (uid, frozen, reason = "") => {
    setFreezing((s) => ({ ...s, [uid]: true }));
    try { await http.post(`/admin/connect/profiles/${uid}/freeze`, { frozen, reason }); toast.success(frozen ? "Account frozen" : "Account unfrozen"); load(); } catch (e) { toast.error(formatErr(e)); } finally { setFreezing((s) => ({ ...s, [uid]: false })); }
  };

  const intentColors = { friendship: "bg-blue-50 text-blue-700", relationship: "bg-rose-50 text-rose-700", marriage: "bg-purple-50 text-purple-700", paused: "bg-gray-100 text-gray-500" };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={frozenOnly} onChange={(e) => setFrozenOnly(e.target.checked)} className="accent-[var(--brand-primary)]" />
          Show frozen accounts only
        </label>
        <button onClick={load} className="text-xs px-3 py-1.5 rounded-lg border border-[var(--border-default)] hover:bg-[var(--bg-subtle)]"><RefreshCw size={12} className="inline mr-1" />Refresh</button>
      </div>
      {loading ? <div className="flex justify-center py-8"><Loader2 size={24} className="animate-spin text-[var(--brand-accent)]" /></div> : profiles.length === 0 ? (
        <div className="card-surface p-8 text-center text-sm text-[var(--text-secondary)]">No connect profiles found</div>
      ) : profiles.map((p) => (
        <div key={p.id} className={`card-surface p-4 space-y-3 ${p.frozen ? "opacity-75 border-red-200 border" : ""}`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[var(--brand-primary)] text-white grid place-items-center font-bold shrink-0">
              {p.user?.avatar ? <img src={p.user.avatar} alt={p.user.name} className="w-full h-full rounded-full object-cover" /> : (p.user?.name || "?")[0]}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm text-[var(--brand-primary)]">{p.user?.name}</span>
                {p.frozen && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold">Frozen</span>}
                {p.verification_status && p.verification_status !== "none" && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">{p.verification_status}</span>}
              </div>
              <div className="text-xs text-[var(--text-tertiary)]">{p.user?.email}</div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${intentColors[p.intent] || "bg-gray-100 text-gray-600"}`}>{p.intent}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="text-xs text-[var(--text-tertiary)]">Verify as:</div>
            {VERIFY_LEVELS.map((level) => (
              <button key={level} disabled={verifying[p.user_id]} onClick={() => verify(p.user_id, level)} className="text-xs px-2 py-1 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                {verifying[p.user_id] ? <Loader2 size={10} className="animate-spin" /> : null} {level}
              </button>
            ))}
            <button disabled={freezing[p.user_id]} onClick={() => freeze(p.user_id, !p.frozen, p.frozen ? "" : "Admin freeze")} className={`text-xs px-2 py-1 rounded-lg border font-medium ${p.frozen ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50" : "border-red-200 text-red-700 hover:bg-red-50"}`}>
              {freezing[p.user_id] ? <Loader2 size={10} className="animate-spin" /> : null} {p.frozen ? "Unfreeze" : "Freeze"}
            </button>
          </div>
          {p.frozen_reason && <div className="text-xs text-red-600">Reason: {p.frozen_reason}</div>}
        </div>
      ))}
    </div>
  );
}

function ConnectMatchesPanel() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [reviewing, setReviewing] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    http.get("/admin/connect/matches", { params: statusFilter ? { status: statusFilter } : {} })
      .then((r) => setMatches(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const reviewMatch = async (matchId, action) => {
    setReviewing((s) => ({ ...s, [matchId]: true }));
    try { await http.post(`/admin/connect/matches/${matchId}/review`, { action }); toast.success(`Match ${action}ged`); load(); } catch (e) { toast.error(formatErr(e)); } finally { setReviewing((s) => ({ ...s, [matchId]: false })); }
  };

  const approveTestimony = async (matchId, action) => {
    try { await http.post(`/admin/connect/matches/${matchId}/approve-testimony`, { action }); toast.success(`Testimony ${action}d`); load(); } catch (e) { toast.error(formatErr(e)); }
  };

  const statusColors = { new: "bg-emerald-100 text-emerald-700", active: "bg-blue-100 text-blue-700", quiet: "bg-amber-100 text-amber-700", expired: "bg-gray-100 text-gray-500", ended: "bg-gray-100 text-gray-500", under_review: "bg-red-100 text-red-700" };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        {["", "new", "active", "quiet", "under_review", "ended", "expired"].map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)} className={`text-xs px-3 py-1.5 rounded-full border ${statusFilter === s ? "bg-[var(--brand-primary)] text-white" : "border-[var(--border-default)] text-[var(--text-secondary)]"}`}>
            {s === "" ? "All" : s.replace("_", " ")}
          </button>
        ))}
      </div>
      {loading ? <div className="flex justify-center py-8"><Loader2 size={24} className="animate-spin text-[var(--brand-accent)]" /></div> : matches.length === 0 ? (
        <div className="card-surface p-8 text-center text-sm text-[var(--text-secondary)]">No matches found</div>
      ) : matches.map((m) => (
        <div key={m.id} className="card-surface p-4 space-y-3">
          <div className="flex items-center gap-3">
            <div className="text-sm font-medium text-[var(--brand-primary)]">{m.user1?.name} ↔ {m.user2?.name}</div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${statusColors[m.status] || "bg-gray-100 text-gray-600"}`}>{m.status}</span>
            {m.relationship_status && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700">{m.relationship_status}</span>}
          </div>
          <div className="text-xs text-[var(--text-tertiary)]">
            Matched {new Date(m.matched_at).toLocaleString()} · Intents: {m.intent_user1} / {m.intent_user2}
            {m.last_message_at && ` · Last message: ${new Date(m.last_message_at).toLocaleString()}`}
          </div>
          {m.testimony && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-1">
              <div className="text-xs font-semibold text-purple-700">Testimony — {m.testimony.status}</div>
              <div className="text-xs text-purple-600 font-medium">{m.testimony.title}</div>
              <p className="text-xs text-purple-600 line-clamp-2">{m.testimony.story}</p>
              {m.testimony.status === "pending" && (
                <div className="flex gap-2 mt-2">
                  <button onClick={() => approveTestimony(m.id, "approve")} className="text-xs px-2 py-1 rounded-lg bg-emerald-100 text-emerald-700 border border-emerald-200">Approve</button>
                  <button onClick={() => approveTestimony(m.id, "reject")} className="text-xs px-2 py-1 rounded-lg bg-red-50 text-red-600 border border-red-200">Reject</button>
                </div>
              )}
            </div>
          )}
          <div className="flex gap-2 flex-wrap">
            {MATCH_ACTIONS.map((action) => (
              <button key={action} disabled={reviewing[m.id]} onClick={() => reviewMatch(m.id, action)} className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors ${action === "end" ? "border-red-200 text-red-700 hover:bg-red-50" : action === "flag" ? "border-amber-200 text-amber-700 hover:bg-amber-50" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"}`}>
                {reviewing[m.id] && <Loader2 size={10} className="animate-spin inline mr-1" />}{action.charAt(0).toUpperCase() + action.slice(1)}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ConnectAuditPanel() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    http.get("/admin/connect/audit").then((r) => setLogs(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const eventColor = {
    match_created: "text-emerald-600",
    match_ended: "text-red-500",
    user_reported: "text-amber-600",
    report_resolved_freeze: "text-red-700",
    report_resolved_ban: "text-red-900",
    profile_verified: "text-blue-600",
    profile_frozen: "text-red-600",
    profile_unfrozen: "text-emerald-600",
    settings_updated: "text-purple-600",
    message_hard_blocked: "text-red-700",
    relationship_status_updated: "text-purple-600",
  };

  return (
    <div className="space-y-2">
      {loading ? <div className="flex justify-center py-8"><Loader2 size={24} className="animate-spin text-[var(--brand-accent)]" /></div> : logs.length === 0 ? (
        <div className="card-surface p-8 text-center text-sm text-[var(--text-secondary)]">No audit events yet</div>
      ) : logs.map((l) => (
        <div key={l.id} className="card-surface px-3 py-2 flex items-start gap-3 text-xs">
          <span className={`font-mono font-medium shrink-0 ${eventColor[l.event] || "text-[var(--text-primary)]"}`}>{l.event}</span>
          <div className="flex-1 text-[var(--text-tertiary)] truncate">
            {l.match_id && <span>match:{l.match_id.slice(-6)} </span>}
            {l.target_id && <span>target:{l.target_id.slice(-6)} </span>}
            {l.actor_id && <span>by:{l.actor_id.slice(-6)} </span>}
            {l.level && <span>level:{l.level} </span>}
            {l.status && <span>status:{l.status} </span>}
            {l.reason && <span>"{l.reason}" </span>}
          </div>
          <span className="text-[var(--text-tertiary)] shrink-0">{new Date(l.created_at).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

// ── CSV helpers ───────────────────────────────────────────────────────────────
const CSV_COLUMNS = [
  "name","gender","city","country","ccc_rank","intent","bio","avatar_url",
  "favorite_hymn","choir_experience","service_preference","spiritual_habits",
  "icebreaker_1","icebreaker_2","learn_hymns_together","study_constitution_together","cpmwave_interest",
];

const CSV_SAMPLE_ROWS = [
  ["Adaeze Okonkwo","female","Lagos","Nigeria","Prophetess","friendship","A devoted Celestial sister who loves early morning prayers and hymn singing.","https://i.pravatar.cc/150?img=47","Hymn 91 — Praise God from Whom All Blessings Flow","Alto in the senior choir, 8 years","Intercessory prayer team","Daily 4am prayer + Bible study","What is your favourite CCC hymn and why?","How long have you been a member of CCC?","true","false","true"],
  ["Emmanuel Adeyemi","male","Ibadan","Nigeria","Elder","marriage","Committed Celestial brother seeking a God-fearing partner for courtship and marriage.","https://i.pravatar.cc/150?img=12","Hymn 1 — Holy Holy Holy","Tenor in the choir, 5 years","Ushering and protocol department","Morning devotion and Sunday school","What role do you serve in your parish?","Do you play any musical instruments?","false","true","false"],
  ["Ngozi Eze","female","Enugu","Nigeria","Senior Prophetess","relationship","Prayerful and cheerful sister who enjoys choir and women's ministry.","https://i.pravatar.cc/150?img=48","Hymn 45 — Nearer My God to Thee","Soprano, choir director assistant, 12 years","Women's fellowship and protocol","Evening devotion with psalms","What ministry or service do you enjoy most in church?","What does your daily prayer life look like?","true","true","true"],
  ["Biodun Fashola","male","Abuja","Nigeria","Deacon","friendship","Serving in the music ministry. Passionate about CPM Wave and hymn evangelism.","https://i.pravatar.cc/150?img=15","Hymn 200 — Amazing Grace (CCC version)","Keyboard player and choir leader, 10 years","Music ministry and CPM Wave","Morning prayer and scripture meditation","Is there a hymn you would love to learn this year?","What is your favourite CCC hymn and why?","true","false","true"],
  ["Chioma Osei","female","Accra","Ghana","Prophetess","marriage","A quiet and prayerful sister who values spiritual growth and godly courtship.","https://i.pravatar.cc/150?img=49","Hymn 77 — What a Friend We Have in Jesus","Soprano in the women's choir, 6 years","Prayer warrior and Sunday school teacher","4am prayer class every Wednesday and Sunday","How long have you been a member of CCC?","What ministry or service do you enjoy most in church?","false","true","false"],
];

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];
  const headers = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, ""));
  return lines.slice(1).map((line) => {
    const values = [];
    let cur = "", inQuote = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { inQuote = !inQuote; }
      else if (ch === "," && !inQuote) { values.push(cur); cur = ""; }
      else { cur += ch; }
    }
    values.push(cur);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = (values[i] || "").trim(); });
    return obj;
  }).filter((r) => r.name);
}

function downloadCSVTemplate() {
  const header = CSV_COLUMNS.join(",");
  const rows = CSV_SAMPLE_ROWS.map((r) => r.map((v) => `"${v}"`).join(","));
  const blob = new Blob([[header, ...rows].join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "celestial_match_seed_users.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 150);
}

// ── Seed Profiles Panel ───────────────────────────────────────────────────────
function ConnectSeedProfilesPanel() {
  const [preview, setPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [dummies, setDummies] = useState([]);
  const [loadingDummies, setLoadingDummies] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genCount, setGenCount] = useState(5);
  const [genGender, setGenGender] = useState("mixed");
  const [selected, setSelected] = useState(new Set());
  const [editingAvatar, setEditingAvatar] = useState(null);
  const fileRef = React.useRef(null);

  const loadDummies = useCallback(async () => {
    setLoadingDummies(true);
    try {
      const { data } = await http.get("/admin/connect/dummy-users");
      setDummies(data);
    } catch (e) { toast.error(formatErr(e)); } finally { setLoadingDummies(false); }
  }, []);

  useEffect(() => { loadDummies(); }, [loadDummies]);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const parsed = parseCSV(ev.target.result);
      setPreview(parsed);
      setSelected(new Set(parsed.map((_, i) => i)));
      setResult(null);
    };
    reader.readAsText(file);
  };

  const generateWithAI = async () => {
    setGenerating(true);
    try {
      const { data } = await http.post("/admin/connect/ai-generate-profiles", { count: genCount, gender: genGender });
      setPreview(data);
      setSelected(new Set(data.map((_, i) => i)));
      setResult(null);
      toast.success(`AI drafted ${data.length} profiles — review below, add images, then approve`);
    } catch (e) { toast.error(formatErr(e)); } finally { setGenerating(false); }
  };

  const toggleSelect = (i) => setSelected((s) => { const n = new Set(s); n.has(i) ? n.delete(i) : n.add(i); return n; });
  const toggleAll = () => setSelected((s) => s.size === preview.length ? new Set() : new Set(preview.map((_, i) => i)));
  const editRow = (i, key, val) => setPreview((p) => p.map((r, idx) => idx === i ? { ...r, [key]: val } : r));
  const removeRow = (i) => { setPreview((p) => p.filter((_, idx) => idx !== i)); setSelected((s) => { const n = new Set([...s].filter((x) => x !== i).map((x) => x > i ? x - 1 : x)); return n; }); };

  const doImport = async () => {
    const toImport = preview.filter((_, i) => selected.has(i));
    if (!toImport.length) { toast.error("Select at least one profile to import"); return; }
    setImporting(true);
    try {
      const { data } = await http.post("/admin/connect/import-dummy-users", { users: toImport });
      setResult(data);
      setPreview([]);
      setSelected(new Set());
      if (fileRef.current) fileRef.current.value = "";
      await loadDummies();
      toast.success(`Approved & imported ${data.created} seed profile${data.created !== 1 ? "s" : ""}!`);
    } catch (e) { toast.error(formatErr(e)); } finally { setImporting(false); }
  };

  const saveAvatar = async () => {
    if (!editingAvatar) return;
    try {
      await http.patch(`/admin/connect/dummy-users/${editingAvatar.id}/avatar`, { avatar_url: editingAvatar.url });
      toast.success("Image updated");
      setEditingAvatar(null);
      loadDummies();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const deleteOne = async (userId, name) => {
    if (!window.confirm(`Remove seed profile: ${name}?`)) return;
    try {
      await http.delete(`/admin/connect/dummy-users/${userId}`);
      toast.success("Removed");
      loadDummies();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const deleteAll = async () => {
    if (!window.confirm(`Delete ALL ${dummies.length} seed profiles? This cannot be undone.`)) return;
    setDeletingAll(true);
    try {
      const { data } = await http.delete("/admin/connect/dummy-users");
      toast.success(`Deleted ${data.deleted} seed profiles`);
      setDummies([]);
    } catch (e) { toast.error(formatErr(e)); } finally { setDeletingAll(false); }
  };

  return (
    <div className="space-y-6">

      {/* ── AI Generate ─────────────────────────────────────── */}
      <div className="card-surface p-5 space-y-4 border-2 border-[var(--brand-accent)]/25">
        <div className="flex items-center gap-2 flex-wrap">
          <Bot size={18} className="text-[var(--brand-accent)]" />
          <div className="font-semibold text-[var(--brand-primary)]">Generate Profiles with AI</div>
          <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--brand-accent)]/10 text-[var(--brand-accent)] font-semibold border border-[var(--brand-accent)]/20">Recommended</span>
        </div>
        <p className="text-sm text-[var(--text-secondary)]">AI writes realistic CCC member profiles — names, bios, hymns, service roles, icebreakers. You review each draft, add photos, and approve before anything goes live.</p>
        <div className="flex gap-4 flex-wrap items-center">
          <label className="text-sm font-medium text-[var(--text-secondary)]">
            How many?
            <select value={genCount} onChange={(e) => setGenCount(+e.target.value)} className="ml-2 input-clean w-auto text-sm py-1.5 px-2">
              {[3, 5, 8, 10, 15, 20].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <label className="text-sm font-medium text-[var(--text-secondary)]">
            Gender:
            <select value={genGender} onChange={(e) => setGenGender(e.target.value)} className="ml-2 input-clean w-auto text-sm py-1.5 px-2">
              <option value="mixed">Mixed</option>
              <option value="female">Female only</option>
              <option value="male">Male only</option>
            </select>
          </label>
          <button onClick={generateWithAI} disabled={generating} className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold">
            {generating ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
            {generating ? "AI is writing profiles…" : "Generate Drafts"}
          </button>
        </div>
        <p className="text-xs text-[var(--text-tertiary)]">Requires AI API key set in Admin → Settings → ai_api_key (free Groq key from console.groq.com works).</p>
      </div>

      {/* ── CSV Upload ──────────────────────────────────────── */}
      <div className="card-surface p-5 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="font-semibold text-sm text-[var(--brand-primary)]">Or Upload CSV</div>
          <button onClick={downloadCSVTemplate} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-[var(--brand-accent)] text-[var(--brand-accent)] font-semibold hover:bg-[var(--brand-accent)]/5 transition-colors">
            <Download size={12} /> Download Template
          </button>
        </div>
        <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile}
          className="block w-full text-sm text-[var(--text-secondary)] file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border file:border-[var(--border-default)] file:text-sm file:font-semibold file:bg-[var(--bg-subtle)] file:text-[var(--brand-primary)] hover:file:bg-[var(--bg-paper)] cursor-pointer" />
      </div>

      {/* ── Draft Review Table ──────────────────────────────── */}
      {preview.length > 0 && (
        <div className="card-surface p-5 space-y-4">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <div className="font-semibold text-[var(--brand-primary)]">Review & Approve Drafts</div>
              <div className="text-xs text-[var(--text-secondary)] mt-0.5">
                {selected.size} of {preview.length} selected · Edit name/intent inline · Paste image URLs in the Photo column · Uncheck rows to skip
              </div>
            </div>
            <button onClick={toggleAll} className="text-xs px-3 py-1.5 rounded-lg border border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)] whitespace-nowrap">
              {selected.size === preview.length ? "Deselect all" : "Select all"}
            </button>
          </div>

          <div className="overflow-x-auto rounded-xl border border-[var(--border-default)]">
            <table className="w-full text-xs">
              <thead className="bg-[var(--bg-subtle)] sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2.5 w-8">
                    <input type="checkbox" checked={selected.size === preview.length && preview.length > 0} onChange={toggleAll} className="accent-[var(--brand-primary)]" />
                  </th>
                  {["Photo / URL", "Name", "Location", "Intent", "Bio / Rank", ""].map((h) => (
                    <th key={h} className="px-3 py-2.5 text-left font-semibold text-[var(--text-secondary)] whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.map((row, i) => (
                  <tr key={i} className={`border-t border-[var(--border-default)] transition-opacity ${!selected.has(i) ? "opacity-40" : "hover:bg-[var(--bg-subtle)]/30"}`}>
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={selected.has(i)} onChange={() => toggleSelect(i)} className="accent-[var(--brand-primary)]" />
                    </td>
                    {/* Avatar + URL input */}
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2" style={{ minWidth: 200 }}>
                        {row.avatar_url
                          ? <img src={row.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover shrink-0 border border-[var(--border-default)]" />
                          : <div className="w-8 h-8 rounded-full bg-[var(--bg-subtle)] grid place-items-center text-[var(--text-tertiary)] shrink-0 border border-dashed border-[var(--border-default)]"><Image size={11} /></div>
                        }
                        <input
                          value={row.avatar_url || ""}
                          onChange={(e) => editRow(i, "avatar_url", e.target.value)}
                          placeholder="Paste image URL…"
                          className="text-xs border border-[var(--border-default)] rounded-lg px-2 py-1.5 flex-1 min-w-[130px] focus:outline-none focus:border-[var(--brand-primary)] bg-transparent"
                        />
                      </div>
                    </td>
                    {/* Editable name */}
                    <td className="px-3 py-2" style={{ minWidth: 150 }}>
                      <input
                        value={row.name || ""}
                        onChange={(e) => editRow(i, "name", e.target.value)}
                        className="font-semibold text-[var(--brand-primary)] bg-transparent border-b border-transparent hover:border-[var(--border-default)] focus:border-[var(--brand-primary)] focus:outline-none w-full"
                      />
                      <div className="text-[var(--text-tertiary)] capitalize mt-0.5">{row.gender} · {row.ccc_rank}</div>
                    </td>
                    <td className="px-3 py-2 text-[var(--text-secondary)] whitespace-nowrap">{[row.city, row.country].filter(Boolean).join(", ")}</td>
                    {/* Editable intent */}
                    <td className="px-3 py-2">
                      <select
                        value={row.intent || "friendship"}
                        onChange={(e) => editRow(i, "intent", e.target.value)}
                        className="text-xs border border-[var(--border-default)] rounded-lg px-2 py-1 bg-transparent focus:outline-none focus:border-[var(--brand-primary)]"
                      >
                        <option value="friendship">Friendship</option>
                        <option value="relationship">Relationship</option>
                        <option value="marriage">Marriage</option>
                      </select>
                    </td>
                    <td className="px-3 py-2 text-[var(--text-secondary)]" style={{ maxWidth: 200 }}>
                      <div className="line-clamp-2" title={row.bio}>{row.bio}</div>
                    </td>
                    {/* Remove row */}
                    <td className="px-2 py-2">
                      <button onClick={() => removeRow(i)} className="p-1.5 rounded-lg hover:bg-red-50 text-[var(--text-tertiary)] hover:text-red-500 transition-colors" title="Remove this draft">
                        <XCircle size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {result && (
            <div className={`text-sm rounded-xl px-4 py-3 font-medium ${result.errors?.length ? "bg-amber-50 border border-amber-200 text-amber-800" : "bg-emerald-50 border border-emerald-200 text-emerald-800"}`}>
              ✓ {result.created} imported successfully
              {result.errors?.length > 0 && <ul className="mt-1 space-y-0.5 font-normal">{result.errors.map((e, idx) => <li key={idx} className="text-xs">• {e}</li>)}</ul>}
            </div>
          )}

          <button onClick={doImport} disabled={importing || selected.size === 0}
            className="btn-primary w-full inline-flex items-center justify-center gap-2 py-3 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
            {importing ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
            {importing ? "Importing…" : `Approve & Import ${selected.size} Profile${selected.size !== 1 ? "s" : ""}`}
          </button>
        </div>
      )}

      {/* ── Live Seed Profiles ──────────────────────────────── */}
      <div className="card-surface p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="font-semibold text-sm text-[var(--brand-primary)]">
            Live Seed Profiles
            {dummies.length > 0 && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-[var(--bg-subtle)] text-[var(--text-secondary)] font-normal">{dummies.length}</span>}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadDummies} className="p-2 rounded-xl hover:bg-[var(--bg-subtle)] text-[var(--text-secondary)] transition-colors" title="Refresh">
              <RefreshCw size={14} className={loadingDummies ? "animate-spin" : ""} />
            </button>
            {dummies.length > 0 && (
              <button onClick={deleteAll} disabled={deletingAll} className="text-xs px-3 py-1.5 rounded-xl bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 font-semibold transition-colors inline-flex items-center gap-1">
                {deletingAll ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Delete All
              </button>
            )}
          </div>
        </div>

        {loadingDummies ? (
          <div className="flex justify-center py-8"><Loader2 size={24} className="animate-spin text-[var(--brand-accent)]" /></div>
        ) : dummies.length === 0 ? (
          <div className="text-center py-8 text-sm text-[var(--text-tertiary)]">No seed profiles yet. Use AI generation or upload a CSV above.</div>
        ) : (
          <div className="space-y-2">
            {dummies.map((u) => (
              <div key={u.id} className="rounded-xl border border-[var(--border-default)] overflow-hidden">
                <div className="flex items-center gap-3 p-3">
                  {u.avatar
                    ? <img src={u.avatar} alt={u.name} className="w-11 h-11 rounded-full object-cover shrink-0" />
                    : <div className="w-11 h-11 rounded-full bg-[var(--brand-primary)] text-white grid place-items-center text-sm font-bold shrink-0">{(u.name || "?")[0]}</div>
                  }
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-[var(--brand-primary)] truncate">{u.name}</span>
                      <span className="text-xs text-[var(--text-tertiary)]">{u.ccc_rank}</span>
                      {!u.avatar && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 font-medium">No image</span>
                      )}
                    </div>
                    <div className="text-xs text-[var(--text-secondary)]">{[u.city, u.country].filter(Boolean).join(", ")}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => setEditingAvatar({ id: u.id, url: u.avatar || "" })}
                      className="p-2 rounded-xl hover:bg-[var(--bg-subtle)] text-[var(--text-secondary)] hover:text-[var(--brand-primary)] transition-colors" title="Edit image">
                      <Image size={14} />
                    </button>
                    <button onClick={() => deleteOne(u.id, u.name)}
                      className="p-2 rounded-xl hover:bg-red-50 text-[var(--text-tertiary)] hover:text-red-600 transition-colors" title="Remove">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                {editingAvatar?.id === u.id && (
                  <div className="flex gap-2 items-center px-4 py-3 bg-[var(--bg-subtle)] border-t border-[var(--border-default)]">
                    <Image size={14} className="text-[var(--text-tertiary)] shrink-0" />
                    <input
                      value={editingAvatar.url}
                      onChange={(e) => setEditingAvatar((a) => ({ ...a, url: e.target.value }))}
                      placeholder="https://example.com/photo.jpg"
                      className="input-clean flex-1 text-sm"
                      autoFocus
                      onKeyDown={(e) => { if (e.key === "Enter") saveAvatar(); if (e.key === "Escape") setEditingAvatar(null); }}
                    />
                    <button onClick={saveAvatar} className="px-3 py-1.5 bg-[var(--brand-primary)] text-white rounded-xl text-sm font-semibold whitespace-nowrap">Save Image</button>
                    <button onClick={() => setEditingAvatar(null)} className="px-3 py-1.5 rounded-xl text-sm text-[var(--text-secondary)] hover:bg-[var(--border-default)] whitespace-nowrap">Cancel</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function AlleluiaConnectAdminContent() {
  const [subTab, setSubTab] = useState("settings");

  const SUB_TABS = [
    { k: "settings",          l: "Settings",         icon: Shield },
    { k: "keywords",          l: "Keyword Rules",     icon: FileWarning },
    { k: "reports",           l: "Reports",           icon: Flag },
    { k: "flagged-messages",  l: "Flagged Messages",  icon: AlertCircle },
    { k: "profiles",          l: "Profiles",          icon: Users },
    { k: "matches",           l: "Matches",           icon: HeartHandshake },
    { k: "audit",             l: "Audit Log",         icon: History },
    { k: "seed",              l: "Seed Profiles",     icon: Upload },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-xl bg-[var(--brand-primary)] grid place-items-center">
          <HeartHandshake size={18} className="text-[var(--brand-accent)]" />
        </div>
        <div>
          <div className="font-display text-xl text-[var(--brand-primary)]">Celestial Match Admin</div>
          <div className="text-xs text-[var(--text-secondary)]">Manage faith-centered connections, moderation, and safety</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 border-b border-[var(--border-default)] pb-3">
        {SUB_TABS.map(({ k, l, icon: Icon }) => (
          <button key={k} onClick={() => setSubTab(k)} className={`text-xs px-3 py-1.5 rounded-lg border inline-flex items-center gap-1.5 transition-colors ${subTab === k ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]" : "border-[var(--border-default)] text-[var(--text-secondary)] hover:border-[var(--brand-primary)]"}`}>
            <Icon size={12} /> {l}
          </button>
        ))}
      </div>

      {subTab === "settings" && <ConnectSettingsPanel />}
      {subTab === "keywords" && <ConnectKeywordsPanel />}
      {subTab === "reports" && <ConnectReportsPanel />}
      {subTab === "flagged-messages" && <ConnectFlaggedMessagesPanel />}
      {subTab === "profiles" && <ConnectProfilesPanel />}
      {subTab === "matches" && <ConnectMatchesPanel />}
      {subTab === "audit" && <ConnectAuditPanel />}
      {subTab === "seed" && <ConnectSeedProfilesPanel />}
    </div>
  );
}

export default function Admin() {
  const [tab, setTab] = useState("settings");
  const [pendingParishCount, setPendingParishCount] = useState(0);

  useEffect(() => {
    http.get("/parishes", { params: { limit: 500 } })
      .then((r) => setPendingParishCount(r.data.filter((p) => p.status === "pending_review").length))
      .catch(() => {});
  }, []);

  const TABS = [
    { k: "settings",       l: "Settings & Catalog",        icon: BadgeCheck },
    { k: "parish-import",  l: "Parish Import",             icon: Upload },
    { k: "ranks-import",   l: "Ranks & Catalogs Import",   icon: Download },
    { k: "parishes",       l: "Parishes",                  icon: Plus,     badge: pendingParishCount },
    { k: "approvals",      l: "Approvals",                 icon: BadgeCheck },
    { k: "parish-admin-requests", l: "Parish Admin Requests", icon: UserCog },
    { k: "users",          l: "Users & Roles",             icon: Shield },
    { k: "endorsements",   l: "Shepherd Endorsements",     icon: ShieldCheck },
    { k: "moderation",     l: "Moderation",                icon: FileWarning },
    { k: "prayer-mod",     l: "Prayer Wall",               icon: Heart },
    { k: "choir-hub",      l: "Choir Hub",                 icon: Music },
    { k: "ai-knowledge",   l: "AI Knowledge Base",         icon: Bot },
    { k: "hymns",          l: "CCC Hymns",                 icon: BookOpen },
    { k: "daily-posts",    l: "Daily Posts",               icon: Sparkles },
    { k: "cpmwave",        l: "CPM Wave",                  icon: Music },
    { k: "integrations",   l: "Integrations",              icon: Zap },
    { k: "audit",          l: "Audit Log",                 icon: History },
    { k: "contests",       l: "Contests",                  icon: Trophy },
    { k: "cpm-stars",      l: "CPM Stars",                 icon: Crown },
    { k: "store",          l: "CPM Store",                 icon: ShoppingBag },
    { k: "marketplace",   l: "Marketplace",               icon: Building2 },
    { k: "celestial-match", l: "Celestial Match",             icon: HeartHandshake },
  ];
  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
      <div>
        <h1 className="font-display text-3xl text-[var(--brand-primary)]">Admin Console</h1>
        <p className="text-sm text-[var(--text-secondary)] mt-1 max-w-lg">
          Central console for all parish, community, store, and marketplace administration.
        </p>
      </div>
      <div className="flex flex-wrap gap-1.5 border-b border-[var(--border-default)] pb-4">
        {TABS.map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-3 py-1.5 text-sm rounded-md border inline-flex items-center gap-1.5 transition-colors ${
              tab === t.k
                ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]"
                : "border-[var(--border-default)] text-[var(--text-secondary)] hover:border-[var(--brand-primary)]"
            }`}
          >
            <t.icon size={14} />
            {t.l}
            {t.badge > 0 && (
              <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${tab === t.k ? "bg-amber-400 text-[var(--brand-primary)]" : "bg-amber-500 text-white"}`}>
                {t.badge}
              </span>
            )}
          </button>
        ))}
      </div>
      {tab === "settings" && <SettingsManager />}
      {tab === "parish-import" && <ParishImportManager />}
      {tab === "ranks-import" && <SettingsImportManager />}
      {tab === "parishes" && <ParishesManager />}
      {tab === "approvals" && <ApprovalsManager />}
      {tab === "parish-admin-requests" && <ParishAdminRequestsManager />}
      {tab === "users" && <UsersManager />}
      {tab === "endorsements" && <ShepherdEndorsementManager />}
      {tab === "moderation" && <ModerationManager />}
      {tab === "prayer-mod" && <PrayerModerationManager />}
      {tab === "choir-hub" && <ChoirHubManager />}
      {tab === "ai-knowledge" && <AIKnowledgeManager />}
      {tab === "hymns" && <HymnImportManager />}
      {tab === "daily-posts" && <DailyPostsManager />}
      {tab === "cpmwave" && <CPMWaveManager />}
      {tab === "integrations" && <IntegrationsManager />}
      {tab === "audit" && <AuditManager />}
      {tab === "contests" && <ContestsManager />}
      {tab === "cpm-stars" && <CpmStarsManager />}
      {tab === "store" && <StoreAdminContent />}
      {tab === "marketplace" && <MarketplaceAdminContent />}
      {tab === "celestial-match" && <AlleluiaConnectAdminContent />}
    </div>
  );
}
